//latest
import React, { useState, useRef, useEffect } from "react";
import { useLazyOtherChargeOptions, applyOtherChargeToEntry, queryHasTwoLetters } from "../utils/otherChargeOptions";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";
import { Modal, Button, Form } from "react-bootstrap";
import toast, { Toaster } from "react-hot-toast";
import Select from "react-select";
import { getUserData, node_imageBaseUrl } from "./const";
import "../assets/css/theme-table-header.css";
import {
  EDIT_PURCHASE_LINE_ERROR_OPTIONS,
  collectLineItemFieldErrors,
  lineCellErrorStyle,
  pruneLineFieldErrors,
} from "../utils/lineItemFieldErrors";
import { useIndiaStateCity } from "../utils/useIndiaStateCity";
import { sanitizeMobileInput } from "../utils/mobileInput";
import { handleRoundOffInputChange } from "../utils/roundOffInput";
import { autoRoundOff } from "../utils/autoRoundOff";
import {
  setupPortalContainers,
  teardownPortalContainers,
} from "../utils/portalContainers";
import {
  listBankAccounts,
  listSuppliers,
  showPurchase,
  updatePurchase,
  listCustomerUsers,
  listProducts,
  showProducts,
  showOrderQueue,
  createCustomerUser,
  isErpApiSuccess,
} from "../erpClient";
import {
  mergeProductGroupOptions,
  searchProductGroupSelectOptions,
  productGroupOptionDisplayName,
} from "../utils/productGroupOptions";
import { numPool } from "../utils/productGroupPool";
import ProductGroupPoolBalance from "../components/ProductGroupPoolBalance";
import PaymentSection from "../components/PaymentSection";

import {
  createPurchasesIdempotencyKey,
  getLegacyPurchaseDetailsFromShowResponse,
  isPurchasesApiSuccess,
  mapPurchasePaymentsForUpdateApi,
  mapShowPaymentsToPurchasePaymentRows,
} from "../utils/purchasesErpAdapter";
import {
  buildPaymentTypeSelectOptionsForRow,
  getPaymentTypeSelectedOptionForRow,
} from "../utils/paymentLinesErpAdapter";
import { mapCustomerUsersToPartyList } from "../utils/customerUsersErpAdapter";
import {
  normalizeProductStockListResponse,
  normalizeProductStockShowResponse,
} from "../utils/productStockErpAdapter";

import { useLocation, useNavigate } from "react-router-dom";
import useFillSalesFromCustomOrder from "../utils/useFillSalesFromCustomOrder";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import OtherAmountModal from "../components/OtherAmountModal";
import {
  calculateOtherAmountAmount as calculateAmount,
  calculateOtherAmountLessWeight as calculateLessWeight,
  sumOtherAmountEntries,
  mapApiChargesToOtherAmountEntries,
  mapOtherAmountEntriesForUpdate,
} from "../utils/otherAmountCalculations";

/** Map ERP `item_rate_on` / `itrateon` to purchase table dropdown: FWt | NWt | GWt */
function mapPurchaseItemRateOnApiToUi(apiValue) {
  const raw = String(apiValue ?? "").trim();
  if (!raw) return "FWt";
  const u = raw.toUpperCase();
  if (u === "GWT" || u === "PER_GRAM_GWT" || u === "GROSS") return "GWt";
  if (
    u === "NWT" ||
    u === "PER_GRAM_NWT" ||
    u === "NET" ||
    u === "PER_GRAM"
  ) {
    return "NWt";
  }
  if (u === "FWT" || u === "FINE_WT" || u === "FINE") return "FWt";
  if (raw === "GWt" || raw === "NWt" || raw === "FWt") return raw;
  return "FWt";
}

// Build safe absolute URLs for images coming from Node API.
// Purchase item images may be stored as:
// - full URL (http...)
// - "upload/..." or "uploads/..."
// - "/upload/..."
// - "product/..." (subpath)
// - plain filename
const getNodeImageCandidates = (rawPath) => {
  if (!rawPath) return [];
  const p = String(rawPath).trim();
  if (!p) return [];

  if (
    p.startsWith("data:") ||
    p.startsWith("blob:") ||
    p.startsWith("http://") ||
    p.startsWith("https://")
  ) {
    return [p];
  }

  const base = String(node_imageBaseUrl || "").replace(/\/+$/, "");
  const normalized = p.replace(/^\/+/, "");

  // If API already includes upload(s) prefix, join with base.
  if (normalized.startsWith("upload/") || normalized.startsWith("uploads/")) {
    return [encodeURI(`${base}/${normalized}`)];
  }

  // If it already looks like a subpath, try base/<path> then base/upload/<path>
  if (normalized.includes("/")) {
    return [
      encodeURI(`${base}/${normalized}`),
      encodeURI(`${base}/upload/${normalized}`),
    ];
  }

  // Plain filename: try common locations
  return [
    encodeURI(`${base}/upload/product/${normalized}`),
    encodeURI(`${base}/upload/${normalized}`),
    encodeURI(`${base}/${normalized}`),
  ];
};

const getNodeImageUrl = (rawPath) => getNodeImageCandidates(rawPath)[0] || null;


const FALLBACK_CITIES_BY_STATE = {
  MH: ["Mumbai", "Pune", "Nagpur", "Nashik", "Aurangabad", "Kolhapur"],
  GJ: ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar"],
  RJ: ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Ajmer", "Bikaner"],
  DL: ["New Delhi", "Dwarka", "Rohini", "Saket", "Karol Bagh", "Lajpat Nagar"],
  KA: ["Bengaluru", "Mysuru", "Mangaluru", "Hubballi", "Belagavi", "Davangere"],
  TN: [
    "Chennai",
    "Coimbatore",
    "Madurai",
    "Salem",
    "Tiruchirappalli",
    "Vellore",
  ],
  TG: [
    "Hyderabad",
    "Warangal",
    "Nizamabad",
    "Khammam",
    "Karimnagar",
    "Sangareddy",
  ],
  WB: ["Kolkata", "Howrah", "Durgapur", "Siliguri", "Asansol", "Kharagpur"],
  UP: ["Lucknow", "Kanpur", "Varanasi", "Agra", "Noida", "Ghaziabad"],
  MP: ["Indore", "Bhopal", "Gwalior", "Jabalpur", "Ujjain", "Sagar"],
};

const SUMMARY_LABEL_STYLE = {
  width: "110px",
  fontSize: "0.85rem",
  marginBottom: 0,
  flexShrink: 0,
  fontWeight: 600,
};

const SUMMARY_COMPACT_LABEL_STYLE = {
  width: "72px",
  fontSize: "0.8rem",
  marginBottom: 0,
  flexShrink: 0,
  fontWeight: 600,
};

const SUMMARY_COMPACT_INPUT_WRAP = {
  flex: 1,
  minWidth: 0,
  maxWidth: "180px",
};

const SUMMARY_COMPACT_DISCOUNT_TYPE_SELECT_STYLE = {
  width: "52px",
  minWidth: "52px",
  flexShrink: 0,
  paddingLeft: "0.4rem",
  paddingRight: "1.1rem",
};

const SUMMARY_COMPACT_NOTE_WRAP = {
  flex: 1,
  minWidth: 0,
  maxWidth: "100%",
};

const PURCHASE_PAYMENT_METHODS = [
  "cash",
  "cheque",
  "bank_transfer",
  "upi",
  "card",
  "old_gold",
  "old_silver",
  "kasar",
  "scheme",
  "order",
  "os",
  "wallet",
];

const PURCHASE_PAYMENT_LABELS = {
  cash: "Cash",
  cheque: "Cheque",
  bank_transfer: "Bank Transfer",
  upi: "UPI",
  card: "Card",
  old_gold: "Old Gold",
  old_silver: "Old Silver",
  kasar: "Kasar",
  scheme: "Scheme",
  order: "Order",
  os: "OS",
  wallet: "Wallet",
};

const READONLY_SUMMARY_INPUT_STYLE = { backgroundColor: "#e8f4fc" };

// React-select styling so Type column matches the Sales UI.
const PURCHASE_TYPE_SELECT_STYLES = {
  control: (base) => ({
    ...base,
    minHeight: "38px",
    height: "38px",
    width: "100%",
    border: "none",
    borderRadius: 0,
    boxShadow: "none",
    backgroundColor: "transparent",
    "&:hover": {
      border: "none",
      cursor: "pointer",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "38px",
    display: "flex",
    alignItems: "center",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: "#000",
  }),
  singleValue: (base) => ({
    ...base,
    margin: 0,
    color: "#000",
    fontSize: "0.85rem",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
    color: "#6c757d",
    display: "none", // matches Sales “Select Type” look
  }),
  indicatorSeparator: (base) => ({
    display: "none",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    fontSize: "0.85rem",
    width: "100%",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "var(--theme-primary)"
      : state.isFocused
        ? "#f8f9fa"
        : "white",
    color: state.isSelected ? "white" : "#000",
    "&:active": {
      backgroundColor: "var(--theme-primary)",
      color: "white",
    },
  }),
  container: (base) => ({
    ...base,
    width: "100%",
    height: "100%",
  }),
};

// Mode should look like Sales “plain cell”: hide dropdown arrow.
const PURCHASE_MODE_SELECT_STYLES = PURCHASE_TYPE_SELECT_STYLES;

/** Product group column: show API name only (id fallback if name missing). */
function formatPurchaseEditProductGroupDisplay(name, rawId) {
  const n =
    name != null && String(name).trim() !== "" ? String(name).trim() : "";
  if (n) return n;
  const idStr =
    rawId !== undefined && rawId !== null && String(rawId).trim() !== ""
      ? String(rawId).trim()
      : "";
  return idStr || "";
}

const defaultPurchasePaymentRows = () => [
  {
    paymentId: null,
    pay_mode: "cash",
    type: "",
    remarks: "",
    amount: "",
    rowKey: "pay-0",
  },
  {
    paymentId: null,
    pay_mode: "upi",
    type: "",
    remarks: "",
    amount: "",
    rowKey: "pay-1",
  },
  {
    paymentId: null,
    pay_mode: "bank_transfer",
    type: "",
    remarks: "",
    amount: "",
    rowKey: "pay-2",
  },
];

const EditPurchase = ({
  masterGoldRate,
  masterSilverRate,
  showLiveRate = true,
}) => {
  const { buttonClass, cardClass, buttonTableStyle } = useButtonTableStyle();
  const navigate = useNavigate();
  const { state } = useLocation();
  const purchaseIdFromState = state?.purchaseId || state?.id || null;
  const [purchaseId, setPurchaseId] = useState(purchaseIdFromState);
  const [purDeId, setPurDeId] = useState(null); // Store purchase_details_id
  const userData = getUserData();
  // Add new function to calculate per gram rate
  const calculatePerGramRate = (rate, metalType) => {
    return rate;
  };

  // Calculate rate based on purity
  const calculatePurityBasedRate = (purity, rate, metalType) => {
    // Extract the karat/purity value from string (e.g. "18K" -> 18 or "925" -> 925)
    const purityValue = parseFloat(purity);
    if (isNaN(purityValue)) return rate;

    if (metalType === "Gold") {
      return rate * (purityValue / 24);
    } else if (metalType === "Silver") {
      return (rate * purityValue) / 999;
    }

    return rate;
  };

  const COLUMNS = [
    { id: "no", label: "No.", width: "20px", value: (row) => row.no },
    {
      id: "metalGroup",
      label: "Product Group",
      width: "120px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.metalGroup}
          onChange={(e) => onChange(row.id, "metalGroup", e.target.value)}
        />
      ),
    },
    {
      id: "pcs",
      label: "Pcs",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.pcs}
          onChange={(e) => onChange(row.id, "pcs", e.target.value)}
        />
      ),
    },
    {
      id: "grWt",
      label: "Gr.Wt",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.grWt}
          onChange={(e) => onChange(row.id, "grWt", e.target.value)}
        />
      ),
    },
    {
      id: "lsWt",
      label: "Ls.Wt",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lsWt}
          onChange={(e) => onChange(row.id, "lsWt", e.target.value)}
        />
      ),
    },
    {
      id: "netWt",
      label: "Net.Wt",
      width: "50px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.netWt}
          onChange={(e) => onChange(row.id, "netWt", e.target.value)}
        />
      ),
    },
    {
      id: "purity",
      label: "Purity",
      width: "60px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.purity}
          onChange={(e) => onChange(row.id, "purity", e.target.value)}
        />
      ),
    },
    {
      id: "wastage",
      label: "Wastage",
      width: "60px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.wastage}
          onChange={(e) => onChange(row.id, "wastage", e.target.value)}
        />
      ),
    },
    {
      id: "fineWt",
      label: "Fine.Wt",
      width: "60px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.fineWt}
          onChange={(e) => onChange(row.id, "fineWt", e.target.value)}
          readOnly
          style={{ backgroundColor: "#f0f0f0" }}
        />
      ),
    },
    {
      id: "itRateOn",
      label: "RateOn",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value="FWt"
          readOnly
          style={{ backgroundColor: "#f0f0f0" }}
        />
      ),
    },
    {
      id: "itRate",
      label: "Rate",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.itRate}
          onChange={(e) => onChange(row.id, "itRate", e.target.value)}
        />
      ),
    },
    {
      id: "itAmt",
      label: "Amount",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.itAmt}
          onChange={(e) => onChange(row.id, "itAmt", e.target.value)}
        />
      ),
    },
    {
      id: "lbrRate",
      label: "L.Rate",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrRate}
          onChange={(e) => onChange(row.id, "lbrRate", e.target.value)}
        />
      ),
    },
    {
      id: "lbrAmt",
      label: "L.Amount",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.lbrAmt}
          onChange={(e) => onChange(row.id, "lbrAmt", e.target.value)}
        />
      ),
    },
    {
      id: "othAmt",
      label: "Other",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.othAmt}
          onChange={(e) => onChange(row.id, "othAmt", e.target.value)}
        />
      ),
    },
    {
      id: "mrp",
      label: "MRP",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.mrp}
          onChange={(e) => onChange(row.id, "mrp", e.target.value)}
        />
      ),
    },
    {
      id: "image",
      label: "Image",
      width: "80px",
      value: (row, onChange) => (
        <input
          type="text"
          value={row.image}
          onChange={(e) => onChange(row.id, "image", e.target.value)}
        />
      ),
    },
    {
      id: "totalAmt",
      label: "TotalAmt",
      width: "100px",
      value: (row, onChange) => (
        <input
          type="number"
          value={row.totalAmt}
          onChange={(e) => onChange(row.id, "totalAmt", e.target.value)}
        />
      ),
    },
  ];

  // Fetch Users from API
  const fetchUserss = async () => {
    try {
      const response = await listCustomerUsers({ search: "" });
      const fetchedUserss = mapCustomerUsersToPartyList(response);
      if (fetchedUserss?.length) {
        fetchedUserss.sort((b, a) => b.id - a.id);
        setUserss(fetchedUserss);
      } else {
        console.error("Unexpected data format:", response);
      }
    } catch (error) {
      console.error("Error fetching User Details:", error);
    }
  };

  // Removed: users list fetch; using show_purchase payload party details.
  // useEffect(() => {
  //   fetchUserss();
  // }, []);

  const applyPoolHeaderFromGroup = (group) => {
    if (!group) {
      setProductGroupPoolHeader(null);
      return;
    }
    setProductGroupPoolHeader({
      pcs: numPool(group.pool_pcs),
      nwt: numPool(group.pool_nwt),
    });
  };

  const getMetalGroupOptionsList = () =>
    mergeProductGroupOptions(
      productGroupOptionsCacheRef.current,
      dropdownData.metalGroup,
    );

  // Search product groups via API when user types 3+ characters
  const fetchMetalGroupByPrefix = async (prefix = "") => {
    const trimmed = String(prefix || "").trim();
    if (trimmed.length < 3) {
      setDropdownData((prev) => ({ ...prev, metalGroup: [] }));
      setMetalGroupApiCalled(false);
      setProductGroupSearchLoading(false);
      return [];
    }

    if (lastSearchedMetalGroupPrefix === trimmed) {
      setMetalGroupApiCalled(true);
      return dropdownData.metalGroup;
    }

    setProductGroupSearchLoading(true);
    try {
      const opts = await searchProductGroupSelectOptions(trimmed);
      productGroupOptionsCacheRef.current = mergeProductGroupOptions(
        productGroupOptionsCacheRef.current,
        opts,
      );
      setDropdownData((prev) => ({
        ...prev,
        metalGroup: opts,
      }));
      setLastSearchedMetalGroupPrefix(trimmed);
      setMetalGroupApiCalled(true);
      if (opts.length > 0) {
        applyPoolHeaderFromGroup(opts[0]);
      }
      return opts;
    } catch (error) {
      console.error("Error searching product groups:", error);
      setDropdownData((prev) => ({ ...prev, metalGroup: [] }));
      setMetalGroupApiCalled(true);
      return [];
    } finally {
      setProductGroupSearchLoading(false);
    }
  };

  // Listen for API URL changes - navigate back to table
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in EditPurchase, navigating back...', event.detail);
      toast('Database switched. Returning to purchase table.');
      navigate('/metalpurchasetable');
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [navigate]);

  const fetchProduct = async () => {
    try {
      const response = await listProducts({
        page: 1,
        limit: 5000,
        search: "",
      });
      const rows = normalizeProductStockListResponse(response);
      if (rows.length) {
        const fetchedProducts = rows.map((product) => ({ id: product.id }));
        fetchedProducts.sort((a, b) => a.id - b.id);
        setProducts(fetchedProducts);
      } else {
        console.error("Unexpected data format:", response);
      }
    } catch (error) {
      console.error("Error fetching product Details:", error);
    }
  };

  // Removed: product list fetch on mount for EditPurchase
  // useEffect(() => {
  //   fetchProduct(); // Fetch product details on component mount
  // }, []);

  // Function removed - tagNo functionality removed from Purchase module

  const fetchPaymentTypes = async () => {
    try {
      const response = await listBankAccounts();
      const rows = Array.isArray(response?.data?.bank_accounts)
        ? response.data.bank_accounts
        : [];

      if (response?.success && Array.isArray(rows)) {
        const fetchedBankDetails = rows.map((bank) => ({
          id: bank.id,
          bankName: bank.bank_name || "",
          accountHolder: bank.holder_name || "",
          accountType: bank.account_type || "",
          ifscCode: bank.ifsc_code || bank.IFSC_code || "",
          accountNumber: bank.account_number || "",
          openingBalance: bank.opening_balance || "",
          balance: bank.opening_balance ? `₹${bank.opening_balance}` : "₹0",
          status: bank.status === 1 ? "Active" : "Inactive",
          statusClass:
            bank.status === 1
              ? "bg-success-subtle text-success"
              : "bg-danger-subtle text-danger",
          useForInvoice: bank.use_for_invoice === 1,
          useForPOS: bank.use_for_pos === 1,
        }));

        fetchedBankDetails.sort((a, b) => b.id - a.id);
        setBankAccounts(fetchedBankDetails);

        const paymentOptions = fetchedBankDetails.map((bank) => ({
          value: bank.id,
          label: bank.accountNumber ? `${bank.bankName}` : bank.bankName,
        }));
        setPaymentTypes(paymentOptions);

        if (fetchedBankDetails.length === 0) {
          toast("No bank accounts found", { icon: "ℹ️" });
        }
      } else if (
        response.data &&
        response.data.message === "radha" &&
        (!response.data.data || response.data.data.length === 0)
      ) {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast("No bank accounts found", { icon: "ℹ️" });
      } else if (response.data && response.data.success === false) {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error(response.data.message || "Failed to fetch bank details");
      } else {
        setBankAccounts([]);
        setPaymentTypes([]);
        toast.error("Unexpected response format from server");
      }
    } catch (error) {
      setBankAccounts([]);
      setPaymentTypes([]);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch bank details"
      );
    }
  };

  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  const [allProducts, setAllProducts] = useState([]);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  /** Full payment lines after load + any user edits; existing rows have paymentId, new rows do not. */
  const [purchasePaymentRows, setPurchasePaymentRows] = useState([]);

  const [payableRows, setPayableRows] = useState([
    {
      id: 1,
      pay_mode: "cash",
      type: "",
      remarks: "",
      amount: "",
    },
  ]);

  const findBankAccountByKeyword = (accounts, keyword) =>
    accounts.find(
      (b) =>
        (b.accountType &&
          (String(b.accountType).toLowerCase() === keyword ||
            String(b.accountType).toLowerCase() ===
              keyword.replace("_", " "))) ||
        (b.bankName &&
          String(b.bankName).toLowerCase().includes(keyword.replace("_", " "))),
    );

  // Resolve bank `type` when accounts load (id match or bank_name label).
  useEffect(() => {
    if (paymentTypes.length === 0 && bankAccounts.length === 0) return;
    const cashAccount = findBankAccountByKeyword(bankAccounts, "cash");
    setPurchasePaymentRows((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        const typeStr =
          row.type != null && String(row.type).trim() !== ""
            ? String(row.type).trim()
            : "";
        const hasValidType =
          typeStr &&
          paymentTypes.some((pt) => String(pt.value) === typeStr);
        if (hasValidType) return row;

        const bankLabel = String(row.bankDisplayName || "").trim();
        if (bankLabel && paymentTypes.length > 0) {
          const lower = bankLabel.toLowerCase();
          const byName = paymentTypes.find((pt) => {
            const label = String(pt.label || "").trim().toLowerCase();
            return (
              label === lower || label.includes(lower) || lower.includes(label)
            );
          });
          if (byName) {
            changed = true;
            return { ...row, type: String(byName.value) };
          }
        }

        if (!typeStr && row.pay_mode === "cash" && cashAccount) {
          changed = true;
          return { ...row, type: String(cashAccount.id) };
        }
        return row;
      });
      return changed ? next : prev;
    });
  }, [bankAccounts, paymentTypes]);

  const updatePurchasePaymentRow = (index, field, raw) => {
    setPurchasePaymentRows((prev) => {
      const next = [...prev];
      const cur = { ...next[index] };
      if (field === "pay_mode") {
        cur.pay_mode = raw;
        cur.type = "";
        if (raw === "cash") {
          const cashAccount = findBankAccountByKeyword(bankAccounts, "cash");
          if (cashAccount) cur.type = String(cashAccount.id);
        }
      } else if (field === "type") {
        cur.type = raw;
      } else if (field === "remarks") {
        cur.remarks = raw;
      } else if (field === "amount") {
        cur.amount = raw;
      }
      next[index] = cur;
      return next;
    });
  };

  const addPurchasePaymentRow = () => {
    setPurchasePaymentRows((prev) => [
      ...prev,
      {
        paymentId: null,
        pay_mode: "bank_transfer",
        type: "",
        remarks: "",
        amount: "",
        rowKey: `pay-n-${Date.now()}`,
      },
    ]);
  };

  // Sales-like UX: when user focuses the last row amount, add one more row.
  const handleAmountFieldFocus = (rowIdx) => {
    if (rowIdx === purchasePaymentRows.length - 1) {
      addPurchasePaymentRow();
    }
  };

  const removePurchasePaymentRow = (index) => {
    setPurchasePaymentRows((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
    );
  };

  // Demo data for dropdowns
  const [dropdownData, setDropdownData] = useState({
    metalGroup: [],
    itRateOn: ["FWt", "NWt", "GWt"], // Adding new options for ItRateOn
  });

  // Columns that should have dropdown functionality
  const dropdownColumns = ["metalGroup", "rateOn", "itRateOn"];

  const initialRowData = COLUMNS.map((col) => ({
    id: col.id,
    value: col.id === "itRateOn" ? "" : "",
    ...(col.id === "image" ? { imageFile: null, imagePreview: null } : {}),
  }));

  const [tableData, setTableData] = useState([{ id: 1, data: initialRowData }]);
  const [itemFieldErrors, setItemFieldErrors] = useState({});

  useEffect(() => {
    setItemFieldErrors((prev) =>
      pruneLineFieldErrors(
        prev,
        collectLineItemFieldErrors(tableData, EDIT_PURCHASE_LINE_ERROR_OPTIONS),
      ),
    );
  }, [tableData]);

  const [activeDropdown, setActiveDropdown] = useState({
    row: null,
    col: null,
    type: null,
  });
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [lastSearchedMetalGroupPrefix, setLastSearchedMetalGroupPrefix] =
    useState("");
  const [metalGroupApiCalled, setMetalGroupApiCalled] = useState(false); // Track if API was called to show "No matching items"
  const [productGroupPoolHeader, setProductGroupPoolHeader] = useState(null);
  const [productGroupSearchLoading, setProductGroupSearchLoading] =
    useState(false);
  const productGroupOptionsCacheRef = useRef([]);
  const dropdownRef = useRef(null);
  const inputRefs = useRef({});
  const dropdownItemsRef = useRef([]);
  const fileInputRefs = useRef({});
  const tooltipRefs = useRef({}); // Add refs for tooltips
  const tableContainerRef = useRef(null);

  // Add state to track active tooltip
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipTimeoutRef = useRef(null);
  const totalAmountInputRef = useRef(null);

  // State for image modal
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    urls: [],
    urlIndex: 0,
    imageUrl: null,
    rowIndex: null,
    colIndex: null,
    zoomLevel: 1,
  });

  // Add state for other amount modal
  const [otherAmountModal, setOtherAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    entries: [],
    currentEntry: {
      chargeType: "",
      pcs: "",
      ct: "",
      wt: "",
      conPercentage: "",
      rateOn: "",
      rate: "",
      amount: "",
    },
    editingIndex: undefined,
  });

  const [totalAmountModal, setTotalAmountModal] = useState({
    isOpen: false,
    rowIndex: null,
    amount: "",
    gst: "3",
  });

  // Add state to track last Enter keypress time
  const [lastEnterPress, setLastEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  const [totalAmountEnterPress, setTotalAmountEnterPress] = useState({
    time: 0,
    rowIndex: null,
  });

  // Update dropdown position after Metal Group API response when dropdown is active
  useEffect(() => {
    if (
      metalGroupApiCalled &&
      activeDropdown.type === "metalGroup" &&
      activeDropdown.row !== null &&
      activeDropdown.col !== null
    ) {
      setTimeout(() => {
        const inputElement =
          inputRefs.current[`${activeDropdown.row}-${activeDropdown.col}`];
        if (inputElement) {
          const rect = inputElement.getBoundingClientRect();
          setDropdownPosition({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
          });
        }
      }, 10);
    }
  }, [metalGroupApiCalled, dropdownData.metalGroup, activeDropdown]);

  useEffect(() => {
    if (
      activeDropdown.type !== "metalGroup" ||
      activeDropdown.row === null ||
      activeDropdown.col === null
    ) {
      return;
    }
    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    const filtered = getFilteredItems(cell?.value, "metalGroup");
    if (filtered[highlightedIndex]) {
      applyPoolHeaderFromGroup(filtered[highlightedIndex]);
    }
  }, [highlightedIndex, activeDropdown, dropdownData.metalGroup]);

  useEffect(() => {
    if (totalAmountModal.isOpen) {
      const timer = setTimeout(() => {
        if (totalAmountInputRef.current) {
          totalAmountInputRef.current.focus();
          totalAmountInputRef.current.select();
        }
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [totalAmountModal.isOpen]);

  // Update the otherAmountColumnWidths state to use fixed widths
  const [otherAmountColumnWidths, setOtherAmountColumnWidths] = useState({
    no: "10px", // Added for No. column
    chargetype: "150px", // Changed from chargeType to match transformation
    pcs: "20px",
    ct: "50px",
    wt: "60px",
    con: "10px",
    rateon: "50px",
    rate: "68px",
    amount: "80px",
  });

  // Add rateOn options
  const rateOnOptions = ["FIXED", "PCS", "CT", "WT"];

  // const [billtype, setBillType] = useState("");
  const [billDate, setBillDate] = useState("");

  // Party (Type / Name / Mobile) selection like AddJangad
  // Party type is always "KAR" (Karigar)
  const partyType = "KAR";
  const [partyName, setPartyName] = useState("");
  const [partyMobile, setPartyMobile] = useState("");
  const [partyId, setPartyId] = useState(null);
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const [partyOptions, setPartyOptions] = useState([]);
  const partyDropdownRef = useRef(null);
  const partyNameInputRef = useRef(null);
  const partyItemsRef = useRef([]);
  const [partyHighlightedIndex, setPartyHighlightedIndex] = useState(0);

  // Fetch karigar options by name prefix (3+ chars)
  const fetchPartyOptionsByPrefix = async (prefix) => {
    try {
      if (!prefix || prefix.length < 3) {
        setPartyOptions([]);
        setShowPartyDropdown(false);
        return;
      }

      let options = [];
      const response = await listSuppliers({ search: prefix, status: 1 });
      if (response?.success) {
        const karigarList = Array.isArray(response.data?.suppliers)
          ? response.data.suppliers
          : [];
        if (karigarList.length > 0) {
          // Filter active karigars (status === 1)
          const activeKarigars = karigarList.filter(
            (karigar) => karigar.status === 1 || karigar.status === "1"
          );

          options = activeKarigars
            .filter((karigar) => {
              // Include if has company_name or name or short_code
              return karigar.company_name || karigar.name || karigar.short_code;
            })
            .map((karigar) => {
              // Use company_name as primary, fallback to name, then short_code
              const displayName =
                karigar.company_name ||
                karigar.name ||
                karigar.short_code ||
                "";
              const contactNumber = karigar.contact_number || "";

              // Build label: "CompanyName (MobileNumber)" - show company name first, then mobile in parentheses
              let label = displayName;
              if (contactNumber) {
                label = `${displayName} (${contactNumber})`;
              }

              return {
                value: contactNumber || karigar.short_code || displayName,
                label: label,
                name: displayName,
                id: karigar.id,
                mobile: contactNumber || "", // Store mobile number separately (empty if no number)
              };
            });
        } else {
          console.warn("No suppliers found in API response:", response);
        }
      }

      // console.log("Final options for dropdown:", options);

      setPartyOptions(options);
      setShowPartyDropdown(options.length > 0);
      setPartyHighlightedIndex(0);
    } catch (error) {
      console.error("Error fetching karigar options by prefix:", error);
      setPartyOptions([]);
      setShowPartyDropdown(false);
    }
  };

  const [tagOptions, setTagOptions] = useState([]);

  // Add customer states
  const [customerMobile, setCustomerMobile] = useState("");
  const [customerMobileSearchText, setCustomerMobileSearchText] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerMobileDropdownActive, setCustomerMobileDropdownActive] =
    useState(false);
  const [customerMobileHighlightedIndex, setCustomerMobileHighlightedIndex] =
    useState(0);
  const customerMobileDropdownRef = useRef(null);
  const customerMobileInputRef = useRef(null);
  const customerMobileItemsRef = useRef([]);

  const [customerName, setCustomerName] = useState("");
  const [customerNameSearchText, setCustomerNameSearchText] = useState("");
  const [customerNameDropdownActive, setCustomerNameDropdownActive] =
    useState(false);
  const [customerNameHighlightedIndex, setCustomerNameHighlightedIndex] =
    useState(0);
  const customerNameDropdownRef = useRef(null);
  const customerNameInputRef = useRef(null);
  const customerNameItemsRef = useRef([]);

  const [customerAddress, setCustomerAddress] = useState("");
  const [customerCity, setCustomerCity] = useState("");
  const [otherChargesValue, setOtherChargesValue] = useState("");

  // Add new state for customer details modal
  const [showCustomerDetailsModal, setShowCustomerDetailsModal] =
    useState(false);
  const [customerDetails, setCustomerDetails] = useState({
    email: "",
    birthDate: "",
    anniversaryDate: "",
    companyName: "",
    companyAddress: "",
    gstNumber: "",
    aadharNo: "",
    panNo: "",
    maritalStatus: "",
    // openingBalance: "",
    // pendingBalance: "",
    balance: "",
    saleBalance: "",
    reference: "",
    state: "",
  });

  const { states, cities, selectedState, setSelectedState } = useIndiaStateCity();

  // Party (Type / Name / Mobile) handlers – search by name prefix (3+ chars)
  const handlePartyNameChange = (e) => {
    const value = e.target.value;
    setPartyName(value);
    setPartyId(null);

    // Clear mobile when name is cleared
    if (!value) {
      setPartyMobile("");
      setPartyOptions([]);
      setShowPartyDropdown(false);
      return;
    }

    // Require at least 3 characters before calling API
    if (value.length < 3) {
      setPartyOptions([]);
      setShowPartyDropdown(false);
      return;
    }

    // Fetch karigar data from API using prefix (no type needed)
    fetchPartyOptionsByPrefix(value);
  };

  const handlePartyNameFocus = () => {
    const input = partyNameInputRef.current;
    if (input) {
      input.style.backgroundColor = "#fff";
    }
    if (partyOptions.length > 0) {
      setShowPartyDropdown(true);
    }
  };

  const handlePartyNameBlur = () => {
    const input = partyNameInputRef.current;
    if (input) {
      input.style.backgroundColor = "transparent";
    }
    setTimeout(() => setShowPartyDropdown(false), 150);
  };

  const handlePartySelect = (option) => {
    // Use mobile number from option if available, otherwise keep empty
    const mobileNumber = option.mobile || "";
    setPartyName(option.name);
    setPartyMobile(mobileNumber); // Use actual mobile number (empty if no number)
    setPartyId(option.id);
    setShowPartyDropdown(false);
    setPartyHighlightedIndex(0);
  };

  const handlePartyNameKeyDown = (e) => {
    const options = partyOptions;
    if (!options.length) return;

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        setShowPartyDropdown(true);
        setPartyHighlightedIndex((prev) => {
          const next = prev < options.length - 1 ? prev + 1 : prev;
          if (partyItemsRef.current[next]) {
            partyItemsRef.current[next].scrollIntoView({
              block: "nearest",
            });
          }
          return next;
        });
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        setPartyHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : 0;
          if (partyItemsRef.current[next]) {
            partyItemsRef.current[next].scrollIntoView({
              block: "nearest",
            });
          }
          return next;
        });
        break;
      }
      case "Enter": {
        if (showPartyDropdown && options[partyHighlightedIndex]) {
          e.preventDefault();
          handlePartySelect(options[partyHighlightedIndex]);
        }
        break;
      }
      case "Escape": {
        e.preventDefault();
        setShowPartyDropdown(false);
        break;
      }
      default:
        break;
    }
  };

  // Close party dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        partyDropdownRef.current &&
        !partyDropdownRef.current.contains(event.target) &&
        partyNameInputRef.current &&
        !partyNameInputRef.current.contains(event.target)
      ) {
        setShowPartyDropdown(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const renderPartyDropdown = () => {
    if (!showPartyDropdown || partyOptions.length === 0) return null;

    const inputElement = partyNameInputRef.current;
    if (!inputElement) return null;

    const inputRect = inputElement.getBoundingClientRect();
    const inputWidth = inputElement.offsetWidth;

    partyItemsRef.current = [];

    const contentWidths = partyOptions.map((option) => {
      const tempSpan = document.createElement("span");
      tempSpan.style.fontSize = "0.85rem";
      tempSpan.style.visibility = "hidden";
      tempSpan.style.position = "absolute";
      tempSpan.style.whiteSpace = "nowrap";
      tempSpan.textContent = option.label || "";
      document.body.appendChild(tempSpan);
      const width = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);
      return width;
    });

    const maxContentWidth =
      contentWidths.length > 0 ? Math.max(...contentWidths) : 0;

    const dropdownWidth = Math.max(
      inputWidth,
      Math.min(maxContentWidth + 24, 280),
      220
    );

    return ReactDOM.createPortal(
      <div
        ref={partyDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom + window.scrollY}px`,
          left: `${inputRect.left + window.scrollX}px`,
          width: `${dropdownWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${dropdownWidth}px`,
          maxHeight: "180px",
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
        }}
      >
        {partyOptions.map((option, idx) => (
          <div
            key={`${option.value}-${idx}`}
            ref={(el) => (partyItemsRef.current[idx] = el)}
            className={`dropdown-item ${
              idx === partyHighlightedIndex ? "active" : ""
            }`}
            style={{
              cursor: "pointer",
              padding: "0.25rem 1rem",
              fontSize: "0.85rem",
              backgroundColor:
                idx === partyHighlightedIndex ? "var(--theme-primary)" : "transparent",
              color: idx === partyHighlightedIndex ? "#fff" : "inherit",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              transition: "background-color 0.2s, color 0.2s",
            }}
            onMouseEnter={() => setPartyHighlightedIndex(idx)}
            onMouseDown={(e) => {
              e.preventDefault();
              handlePartySelect(option);
            }}
          >
            {option.label}
          </div>
        ))}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Add new state for tracking if customer is being added manually
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [customerSaveSuccess, setCustomerSaveSuccess] = useState(false);

  const [userss, setUserss] = useState([]);

  // Add Customer Form States
  // const [fullName, setFullName] = useState("");
  // const [mobile, setMobile] = useState("");
  // const [email, setEmail] = useState("");
  // const [city, setCity] = useState("");
  // const [address, setAddress] = useState("");
  // const [birthDate, setBirthDate] = useState("");
  // const [maritalStatus, setMaritalStatus] = useState("");
  // const [anniversaryDate, setAnniversaryDate] = useState("");
  // const [openingBalance, setOpeningBalance] = useState("");
  // const [pendingBalance, setPendingBalance] = useState("");
  // const [companyName, setCompanyName] = useState("");
  // const [companyAddress, setCompanyAddress] = useState("");
  // const [gstNumber, setGstNumber] = useState("");
  // const [status, setStatus] = useState("active");
  // const [showAddModal, setShowAddModal] = useState(false);

  // Mock customer data - replace with real API data
  const USERS = userss.map((user) => ({
    id: user.id,
    name: user.fullName,
    fullName: user.fullName,
    mobile: user.mobile,
    email: user.email,
    city: user.city,
    address: user.address,
    birthDate: user.birthDate,
    maritalStatus: user.maritalStatus,
    anniversaryDate: user.anniversaryDate,
    balance: user.balance,
    saleBalance: user.saleBalance,
    companyName: user.companyName,
    companyAddress: user.companyAddress,
    gstNumber: user.gstNumber,
    aadharNo: user.aadharNo,
    panNo: user.panNo,
    reference: user.reference,
    state: user.state,
  }));

  const [gstPercentage, setGstPercentage] = useState("");

  const [products, setProducts] = useState([]);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setBillDate(today);
  }, []);

  const handleDateChange = (e) => {
    setBillDate(e.target.value);
  };

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const suggestionTimeoutRef = useRef(null);

  // Update the charge types data to include multiple codes
  const [chargeTypes, ensureOtherCharges] = useLazyOtherChargeOptions();


  // Modify the getSuggestions function to handle multiple codes
  const getSuggestions = (query) => {
    if (queryHasTwoLetters(query)) ensureOtherCharges();
    if (!query || query.length < 1) {
      setSuggestions([]);
      return;
    }

    const searchQuery = query.toLowerCase();

    // First try to match by any code
    let matchedByCode = chargeTypes.filter((charge) =>
      charge.codes.some((code) => code.toLowerCase() === searchQuery)
    );

    // If no exact code match, then try partial matches on codes
    if (matchedByCode.length === 0) {
      matchedByCode = chargeTypes.filter((charge) =>
        charge.codes.some((code) => code.toLowerCase().startsWith(searchQuery))
      );
    }

    // Get matches by name
    const matchedByName = chargeTypes.filter(
      (charge) =>
        charge.name.toLowerCase().includes(searchQuery) &&
        !matchedByCode.some((codeMatch) => codeMatch.name === charge.name)
    );

    // Combine results with code matches first
    const matchedSuggestions = [...matchedByCode, ...matchedByName].map(
      (charge) => `${charge.name} (${charge.codes[0]})`
    );

    setSuggestions(matchedSuggestions);
    setShowSuggestions(true);
    setSelectedSuggestionIndex(0);
  };

  // Add function to handle suggestion selection
  const handleSuggestionSelect = (suggestion, rowIndex = null) => {
    // Extract just the name without the code in brackets
    const chargeName = suggestion.split(" (")[0];

    if (rowIndex !== null) {
      // For existing entries
      const newEntries = [...otherAmountModal.entries];
      newEntries[rowIndex].chargeType = chargeName;
      setOtherAmountModal((prev) => ({
        ...prev,
        entries: newEntries,
      }));
    } else {
      // For current entry
      setOtherAmountModal((prev) => ({
        ...prev,
        currentEntry: {
          ...prev.currentEntry,
          chargeType: chargeName,
        },
      }));
    }
    setShowSuggestions(false);
  };

  // Reset highlighted index when dropdown opens or filter changes
  useEffect(() => {
    if (activeDropdown.row !== null && activeDropdown.col !== null) {
      setHighlightedIndex(0);
      dropdownItemsRef.current = [];
    }
  }, [activeDropdown, tableData]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (dropdownRef.current && dropdownItemsRef.current[highlightedIndex]) {
      dropdownItemsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown({ row: null, col: null, type: null });
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Portal targets for dropdown/modal overlays (body-level, ref-counted).
  useEffect(() => {
    setupPortalContainers();
    return teardownPortalContainers;
  }, []);

  // Add this function to calculate Net Weight
  const calculateNetWeight = (gw, lw) => {
    return gw - lw; // NWt = GWt - LWt
  };

  const calculateFineWeight = (netWt, purity, wastage) => {
    const n = parseFloat(netWt) || 0;
    const p = parseFloat(purity) || 0;
    const w = parseFloat(wastage) || 0;
    return n * ((p + w) / 100); // FineWt = [(purity + wastage)/100] * netWt
  };

  // Helper function to calculate and update itRate for a row based on metal group
  const calculateAndUpdateItRate = (tableData, rowIndex) => {
    let newTableData = [...tableData];
    const metalGroupCell = newTableData[rowIndex].data.find(
      (cell) => cell.id === "metalGroup"
    );

    if (!metalGroupCell || !metalGroupCell.value) {
      return newTableData;
    }

    // Find the selected metal group object to get its purity
    const selectedMetalGroup = getMetalGroupOptionsList().find(
      (mg) =>
        mg.label === metalGroupCell.value ||
        mg.name === metalGroupCell.value ||
        String(mg.value) === String(metalGroupCell.value) ||
        (metalGroupCell.productGroupId != null &&
          metalGroupCell.productGroupId !== "" &&
          String(mg.value) === String(metalGroupCell.productGroupId))
    );

    if (selectedMetalGroup) {
      let purityBasedRate = 0;
      if (selectedMetalGroup.metal === "Gold") {
        if (Array.isArray(masterGoldRate)) {
          // Array contains rates
          // When showLiveRate is true: rates are per 10 grams, so divide by 10
          // When showLiveRate is false: rates are already per gram, use as is
          const label = (selectedMetalGroup.label || "").toLowerCase();
          const karatMatch = label.match(/(\d+)\s*c/);
          let karat = karatMatch ? parseInt(karatMatch[1]) : 24;

          // Handle rose gold - use index 3 if available, otherwise calculate from 24K
          if (label.includes("rose")) {
            if (masterGoldRate[3]) {
              // If rose gold rate exists in array
              if (showLiveRate) {
                // Live rate is per 10 grams, divide by 10
                purityBasedRate = masterGoldRate[3] / 10;
              } else {
                // DB rate is already per gram
                purityBasedRate = masterGoldRate[3];
              }
            } else {
              // Calculate from 24K base rate
              const baseRate = masterGoldRate[0] || 0;
              const baseRatePerGram = showLiveRate ? baseRate / 10 : baseRate;
              purityBasedRate = baseRatePerGram * (18 / 24); // Rose gold is typically 18K
            }
          } else {
            // Use base 24K rate and calculate: (baseRate) * (karat/24)
            const baseRate = masterGoldRate[0] || 0;
            // Convert to per gram if live rate (divide by 10), otherwise use as is
            const baseRatePerGram = showLiveRate ? baseRate / 10 : baseRate;
            purityBasedRate = baseRatePerGram * (karat / 24);
          }
        } else {
          // masterGoldRate is a single value
          // When showLiveRate is true: rate is per 10 grams, so divide by 10
          // When showLiveRate is false: rate is already per gram, use as is
          const ratePerGram = showLiveRate
            ? masterGoldRate / 10
            : masterGoldRate;

          if (selectedMetalGroup.purity) {
            // Extract karat from purity
            const purityValue = parseFloat(selectedMetalGroup.purity);
            if (!isNaN(purityValue)) {
              // If purity is percentage (e.g., 75), convert to karat (18)
              // If purity is already karat (e.g., 18), use as is
              const karat =
                purityValue > 24 ? (purityValue / 100) * 24 : purityValue;
              purityBasedRate = ratePerGram * (karat / 24);
            } else {
              purityBasedRate = ratePerGram;
            }
          } else {
            // Default to 24K if no purity specified
            purityBasedRate = ratePerGram;
          }
        }
      } else {
        // Silver: masterSilverRate
        // When showLiveRate is true: rate is per 1000 grams (1 kg), so divide by 1000
        // When showLiveRate is false: rate is already per gram, use as is
        const ratePerGram = showLiveRate
          ? masterSilverRate / 1000
          : masterSilverRate;
        if (selectedMetalGroup.purity) {
          const purityValue = parseFloat(selectedMetalGroup.purity);
          if (!isNaN(purityValue)) {
            // Silver: (purity/999) * rate_per_gram
            purityBasedRate = (purityValue / 999) * ratePerGram;
          } else {
            purityBasedRate = ratePerGram;
          }
        } else {
          purityBasedRate = ratePerGram;
        }
      }

      // Find and update the itRate cell
      const itRateCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "itRate"
      );

      if (itRateCell) {
        const isOverridden = newTableData[rowIndex].userOverrodeItRate;
        const effectiveRate = isOverridden
          ? parseFloat(itRateCell.value) || 0
          : purityBasedRate;
        if (!isOverridden) {
          itRateCell.value = purityBasedRate.toFixed(2);
        }

        const itRateOnCell = newTableData[rowIndex].data.find(
          (c) => c.id === "itRateOn",
        );
        const netWtCell = newTableData[rowIndex].data.find(
          (c) => c.id === "netWt",
        );
        const grWtCell = newTableData[rowIndex].data.find(
          (c) => c.id === "grWt",
        );
        const fineWtCell = newTableData[rowIndex].data.find(
          (c) => c.id === "fineWt",
        );
        const itRateOn = (itRateOnCell?.value || "")
          .toString()
          .trim()
          .toUpperCase();
        const netWt = parseFloat(netWtCell?.value) || 0;
        const grWt = parseFloat(grWtCell?.value) || 0;
        const fineWt = parseFloat(fineWtCell?.value) || 0;
        const baseWeight =
          itRateOn === "NWT" ? netWt : itRateOn === "GWT" ? grWt : fineWt;

        const itAmtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "itAmt",
        );
        if (itAmtCell) {
          itAmtCell.value = (baseWeight * effectiveRate).toFixed(2);
          calculateTotalAmount(newTableData, rowIndex);
        }
      }
    }

    return newTableData;
  };

  // Function to calculate Labor Amount using Lbr % (based on itRate per gram)
  const calculateLbrAmtWithPercentage = (
    baseWeight,
    lbrPercentage,
    itRatePerGram
  ) => {
    return (
      (lbrPercentage / 100) * baseWeight * (parseFloat(itRatePerGram) || 0)
    );
  };

  // Function to calculate Labor Amount using Lbr Rate
  const calculateLbrAmtWithRate = (baseWeight, lbrRate) => {
    return lbrRate * baseWeight;
  };

  const clearManualAmountOverrides = (row) => {
    if (!row?.data) return { lbrCleared: false, totalCleared: false };

    const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmt");
    let lbrCleared = false;
    if (lbrAmtCell?.isManual) {
      delete lbrAmtCell.isManual;
      lbrCleared = true;
    }

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    let totalCleared = false;
    if (totalAmtCell?.totalAmountDetails?.isManual) {
      delete totalAmtCell.totalAmountDetails;
      totalCleared = true;
    }

    return { lbrCleared, totalCleared };
  };

  // Update the handleInputChange function to calculate Lbr Amt
  const handleInputChange = (rowIndex, colIndex, value, meta = {}) => {
    let newTableData = [...tableData];
    const cell = newTableData[rowIndex].data[colIndex];
    const id = cell.id;

    // Allow manual override of itRate; mark flag and recalc itAmt
    if (id === "itRate") {
      cell.value = value;
      newTableData[rowIndex].userOverrodeItRate = true;
      const manualStatus = clearManualAmountOverrides(newTableData[rowIndex]);
      const itRate = parseFloat(value) || 0;
      const itRateOnCell = newTableData[rowIndex].data.find(
        (c) => c.id === "itRateOn"
      );
      const netWtCell = newTableData[rowIndex].data.find(
        (c) => c.id === "netWt"
      );
      const grWtCell = newTableData[rowIndex].data.find((c) => c.id === "grWt");
      const itAmtCell = newTableData[rowIndex].data.find(
        (c) => c.id === "itAmt"
      );
      if (itAmtCell) {
        const fineWtCell = newTableData[rowIndex].data.find(
          (c) => c.id === "fineWt",
        );
        const itRateOn = (itRateOnCell?.value || "")
          .toString()
          .trim()
          .toUpperCase();
        const netWt = parseFloat(netWtCell?.value) || 0;
        const grWt = parseFloat(grWtCell?.value) || 0;
        const fineWt = parseFloat(fineWtCell?.value) || 0;
        const baseWeight =
          itRateOn === "NWT" ? netWt : itRateOn === "GWT" ? grWt : fineWt;
        itAmtCell.value = (baseWeight * itRate).toFixed(2);
        calculateTotalAmount(newTableData, rowIndex);
      }
      if (manualStatus.totalCleared) {
        calculateTotalAmount(newTableData, rowIndex);
      }
      setTableData(newTableData);
      return;
    }

    // Remove obsolete lbrType handling

    // Check if value matches the validation pattern for this column
    const validationPatterns = {
      pcs: /^\d*$/, // Only numbers
      purity: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      wastage: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lbrRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lbrAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      othAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      mrp: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      grWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      lsWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      netWt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itRate: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      itAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
      totalAmt: /^$|^\d*\.?\d*$/, // Empty or numbers with optional decimal point
    };

    // If pattern exists and value doesn't match, don't update
    if (validationPatterns[id] && !validationPatterns[id].test(value)) {
      return; // Don't update if validation fails
    }

    // Handle metalGroup: fetch options when user types 3+ characters
    if (id === "metalGroup") {
      cell.value = value;
      if (meta.productGroupId != null && meta.productGroupId !== "") {
        cell.productGroupId = meta.productGroupId;
      } else {
        delete cell.productGroupId;
      }
      setTableData(newTableData);

      // Trigger API call when user types 3+ characters
      if (value && value.length >= 3) {
        // Only reset if the new value doesn't start with the last searched prefix
        if (
          !lastSearchedMetalGroupPrefix ||
          value !== lastSearchedMetalGroupPrefix
        ) {
          setMetalGroupApiCalled(false); // Reset flag before API call
          fetchMetalGroupByPrefix(value);
        }
      } else {
        setDropdownData((prev) => ({ ...prev, metalGroup: [] }));
        setLastSearchedMetalGroupPrefix("");
        setMetalGroupApiCalled(false);
        setProductGroupPoolHeader(null);
      }
      return;
    }

    cell.value = value;

    if (id === "itRateOn") {
      const itRateCell = newTableData[rowIndex].data.find(
        (c) => c.id === "itRate",
      );
      const itAmtCell = newTableData[rowIndex].data.find(
        (c) => c.id === "itAmt",
      );
      const fineWtCell = newTableData[rowIndex].data.find(
        (c) => c.id === "fineWt",
      );
      const netWtCell = newTableData[rowIndex].data.find(
        (c) => c.id === "netWt",
      );
      const grWtCell = newTableData[rowIndex].data.find((c) => c.id === "grWt");
      const itRate = parseFloat(itRateCell?.value) || 0;
      const itRateOn = (value || "").toString().trim();
      const baseWeight =
        itRateOn === "NWt"
          ? parseFloat(netWtCell?.value) || 0
          : itRateOn === "GWt"
            ? parseFloat(grWtCell?.value) || 0
            : parseFloat(fineWtCell?.value) || 0;
      if (itAmtCell) {
        itAmtCell.value = (baseWeight * itRate).toFixed(2);
      }
      calculateTotalAmount(newTableData, rowIndex);
      setTableData(newTableData);
      return;
    }

    const shouldClearManualOverrides = [
      "lType",
      "lbrPercentage",
      "lbrRate",
      "grWt",
      "lsWt",
      "netWt",
      "pcs",
      "itRateOn",
      "itRate",
      "othAmt",
    ].includes(id);

    if (shouldClearManualOverrides) {
      const manualStatus = clearManualAmountOverrides(newTableData[rowIndex]);
      if (manualStatus.totalCleared) {
        calculateTotalAmount(newTableData, rowIndex);
      }
    }

    // Helper function to calculate and update fineWt
    const calculateAndUpdateFineWt = (tableData, rowIndex) => {
      const netWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "netWt"
      );
      const purityCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "purity"
      );
      const wastageCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "wastage"
      );
      const fineWtCell = tableData[rowIndex].data.find(
        (cell) => cell.id === "fineWt"
      );

      if (netWtCell && purityCell && wastageCell && fineWtCell) {
        const netWt = parseFloat(netWtCell.value) || 0;
        const purity = parseFloat(purityCell.value) || 0;
        const wastage = parseFloat(wastageCell.value) || 0;
        const fineWt = calculateFineWeight(netWt, purity, wastage);
        fineWtCell.value = fineWt.toFixed(2);
      }
    };

    // Handle calculations based on the changed field
    if (id === "grWt" || id === "lsWt") {
      // Calculate Net Weight when Gross Weight or Less Weight changes
      const gwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "grWt"
      );
      const lwCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lsWt"
      );

      if (gwCell && lwCell) {
        const grWt = parseFloat(gwCell.value) || 0;
        const lsWt = parseFloat(lwCell.value) || 0;
        const netWt = calculateNetWeight(grWt, lsWt);

        // Update the netWt cell
        const netWtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "netWt"
        );
        if (netWtCell) {
          netWtCell.value = netWt.toFixed(2);

          // Calculate fineWt
          calculateAndUpdateFineWt(newTableData, rowIndex);

          // Get fineWt for calculations
          const fineWtCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "fineWt"
          );

          // If itRate exists, recalculate itAmt based on fineWt
          const itRateCell = newTableData[rowIndex].data.find(
            (cell) => cell.id === "itRate"
          );

          if (itRateCell && itRateCell.value) {
            const itRateOnCell = newTableData[rowIndex].data.find(
              (c) => c.id === "itRateOn",
            );
            const netWtCell = newTableData[rowIndex].data.find(
              (c) => c.id === "netWt",
            );
            const grWtCell = newTableData[rowIndex].data.find(
              (c) => c.id === "grWt",
            );
            const u = (itRateOnCell?.value || "")
              .toString()
              .trim()
              .toUpperCase();
            const netWt = parseFloat(netWtCell?.value) || 0;
            const grWt = parseFloat(grWtCell?.value) || 0;
            const fineWt = parseFloat(fineWtCell?.value) || 0;
            const baseWeight =
              u === "NWT" ? netWt : u === "GWT" ? grWt : fineWt;
            const itRate = parseFloat(itRateCell.value) || 0;
            const itAmt = (baseWeight * itRate).toFixed(2);
            const itAmtCell = newTableData[rowIndex].data.find(
              (cell) => cell.id === "itAmt",
            );
            if (itAmtCell) {
              itAmtCell.value = itAmt;
              calculateTotalAmount(newTableData, rowIndex);
            }
          }
        }
      }
    } else if (id === "netWt" || id === "purity" || id === "wastage") {
      // Calculate fineWt when netWt, purity, or wastage changes
      calculateAndUpdateFineWt(newTableData, rowIndex);

      // Get fineWt for calculations
      const fineWtCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "fineWt"
      );

      // Recalculate itRate first (to ensure it's up to date), then calculate itAmt
      const updatedData = calculateAndUpdateItRate(newTableData, rowIndex);
      // Update the state with the updated data
      newTableData = updatedData;

      // Recalculate itAmt using fineWt
      if (fineWtCell) {
        const itRateCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "itRate"
        );
        const itAmtCell = newTableData[rowIndex].data.find(
          (cell) => cell.id === "itAmt"
        );

        if (itRateCell && itAmtCell && itRateCell.value) {
          const itRateOnCell = newTableData[rowIndex].data.find(
            (c) => c.id === "itRateOn",
          );
          const netWtCell = newTableData[rowIndex].data.find(
            (c) => c.id === "netWt",
          );
          const grWtCell = newTableData[rowIndex].data.find(
            (c) => c.id === "grWt",
          );
          const u = (itRateOnCell?.value || "")
            .toString()
            .trim()
            .toUpperCase();
          const netWt = parseFloat(netWtCell?.value) || 0;
          const grWt = parseFloat(grWtCell?.value) || 0;
          const fineWt = parseFloat(fineWtCell.value) || 0;
          const baseWeight =
            u === "NWT" ? netWt : u === "GWT" ? grWt : fineWt;
          const itRate = parseFloat(itRateCell.value) || 0;
          itAmtCell.value = (baseWeight * itRate).toFixed(2);
          calculateTotalAmount(newTableData, rowIndex);
        }
      }

      handleLaborCalculations(newTableData, rowIndex);
    } else if (id === "lType") {
      // Handle LBR type change
      const lTypeCell = newTableData[rowIndex].data.find(
        (cell) => cell.id === "lType"
      );
      if (lTypeCell) {
        lTypeCell.value = value;
      }
    } else if (id === "lbrAmt" || id === "othAmt" || id === "itAmt") {
      // Recalculate totalAmt when any of the amount fields change
      calculateTotalAmount(newTableData, rowIndex);
    }

    // Handle LBR rate calculations
    if (id === "lbrRate") {
      newTableData[rowIndex].lastChangedLaborField = id;
      handleLaborCalculations(newTableData, rowIndex);
    }

    setTableData(newTableData);

    // REMOVED THE PROBLEMATIC CODE THAT WAS CALLING sendTagNoToApi ON EVERY KEYSTROKE
    // The API call should only happen when selecting from dropdown (handled in handleDropdownSelect)

    // If you need to handle barcode scanning, you might want to add a separate function
    // or detect when the input is coming from a barcode scanner vs manual typing
  };

  // Add this function to handle key events on input fields
  const handleKeyPress = (event, rowIndex, colIndex) => {
    // Handle key press events if needed
  };

  // New function to calculate total amount
  const calculateTotalAmount = (tableData, rowIndex) => {
    const itAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "itAmt"
    );
    const lbrAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrAmt"
    );
    const othAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "othAmt"
    );
    const itAmt = parseFloat(itAmtCell?.value) || 0;
    const lbrAmt = parseFloat(lbrAmtCell?.value) || 0;
    const othAmt = parseFloat(othAmtCell?.value) || 0;
    const totalAmt = (itAmt + lbrAmt + othAmt).toFixed(2);

    // Update the totalAmt cell
    const totalAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "totalAmt"
    );
    if (totalAmtCell) {
      if (!totalAmtCell.totalAmountDetails?.isManual) {
        totalAmtCell.value = totalAmt.toString();
        if (totalAmtCell.totalAmountDetails) {
          delete totalAmtCell.totalAmountDetails;
        }
      }
    }
  };

  // Handle image upload
  const handleImageUpload = async (field, rowIndex) => {
    // First create a preview of the image
    const fileReader = new FileReader();
    fileReader.onload = async (e) => {
      // Update the image preview in the table data
      setTableData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData[rowIndex] && updatedData[rowIndex].data) {
          const imageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "image"
          );
          if (imageCell) {
            imageCell.imagePreview = e.target.result;
            imageCell.imageFile = field;
          }
        }
        return updatedData;
      });

      // Image upload disabled — keep local file name as value

      // Update the image name in the table data
      setTableData((prevData) => {
        const updatedData = [...prevData];
        if (updatedData[rowIndex] && updatedData[rowIndex].data) {
          const imageCell = updatedData[rowIndex].data.find(
            (cell) => cell.id === "image"
          );
          if (imageCell) {
            imageCell.value = field?.name || "";
          }
        }
        return updatedData;
      });
    };
    fileReader.readAsDataURL(field);
  };

  // Open image modal
  const openImageModal = (
    imageUrl,
    rawImagePath = null,
    rowIndex = null,
    colIndex = null
  ) => {
    // Clear any active tooltips when opening modal
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }
    setActiveTooltip(null);

    // Hide any visible tooltips
    Object.values(tooltipRefs.current).forEach((tooltip) => {
      if (tooltip) tooltip.style.display = "none";
    });

    const urlCandidates = [];
    if (imageUrl) urlCandidates.push(imageUrl);
    if (rawImagePath) urlCandidates.push(...getNodeImageCandidates(rawImagePath));

    // De-duplicate while preserving order
    const urls = Array.from(
      new Set(urlCandidates.filter((u) => typeof u === "string" && u.trim()))
    );

    setImageModal({
      isOpen: true,
      urls,
      urlIndex: 0,
      imageUrl: urls[0] || null,
      rowIndex,
      colIndex,
      zoomLevel: 1,
    });
  };

  // Close image modal
  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      urls: [],
      urlIndex: 0,
      imageUrl: null,
      rowIndex: null,
      colIndex: null,
      zoomLevel: 1,
    });
  };

  // Render image modal
  const renderImageModal = () => {
    if (!imageModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal show={imageModal.isOpen} onHide={closeImageModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center">
            <img
              src={imageModal.imageUrl}
              alt="Preview"
              onError={() => {
                setImageModal((prev) => {
                  const nextIndex = (prev?.urlIndex ?? 0) + 1;
                  const nextUrl = prev?.urls?.[nextIndex] || null;
                  if (!nextUrl) return prev;
                  return { ...prev, urlIndex: nextIndex, imageUrl: nextUrl };
                });
              }}
              style={{
                maxWidth: "100%",
                maxHeight: "400px",
                objectFit: "contain",
              }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                // Trigger file input to change the image (prefer modal row/col if available)
                const r =
                  imageModal.rowIndex !== null ? imageModal.rowIndex : null;
                const c =
                  imageModal.colIndex !== null ? imageModal.colIndex : null;

                if (r !== null && c !== null) {
                  triggerFileInput(r, c);
                  closeImageModal();
                  return;
                }

                // Fallback: locate by matching any URL candidate
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      imageModal.urls.includes(cell.imagePreview)
                  )
                );

                if (rowIndex !== -1) {
                  const colIndex = tableData[rowIndex].data.findIndex(
                    (cell) => cell.id === "image"
                  );
                  if (colIndex !== -1) {
                    triggerFileInput(rowIndex, colIndex);
                    closeImageModal();
                  }
                }
              }}
              title="Change Image"
            >
              <Icon icon="solar:gallery-edit-bold" width="16" height="16" />
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => {
                const r =
                  imageModal.rowIndex !== null ? imageModal.rowIndex : null;
                if (r !== null) {
                  setTableData((prevData) => {
                    const updatedData = [...prevData];
                    const imageCell = updatedData[r]?.data?.find(
                      (c) => c.id === "image"
                    );
                    if (imageCell) {
                      imageCell.value = "";
                      imageCell.imageFile = null;
                      imageCell.imagePreview = null;
                    }
                    return updatedData;
                  });
                  closeImageModal();
                  return;
                }

                // Fallback: locate by matching any URL candidate
                const rowIndex = tableData.findIndex((row) =>
                  row.data.some(
                    (cell) =>
                      cell.id === "image" &&
                      imageModal.urls.includes(cell.imagePreview)
                  )
                );

                if (rowIndex !== -1) {
                  setTableData((prevData) => {
                    const updatedData = [...prevData];
                    const imageCell = updatedData[rowIndex]?.data?.find(
                      (c) => c.id === "image"
                    );
                    if (imageCell) {
                      imageCell.value = "";
                      imageCell.imageFile = null;
                      imageCell.imagePreview = null;
                    }
                    return updatedData;
                  });
                  closeImageModal();
                }
              }}
              title="Delete Image"
            >
              <Icon icon="solar:trash-bin-trash-bold" width="16" height="16" />
            </Button>
          </div>
        </Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal")
    );
  };

  const handleAddRow = () => {
    // Flag to prevent multiple simultaneous calls
    if (window.isAddingRow) return;
    window.isAddingRow = true;

    // Get the current length before adding
    const newRowIndex = tableData.length;

    setTableData((prevData) => [
      ...prevData,
      {
        id: prevData.length + 1,
        data: COLUMNS.map((col) => ({
          id: col.id,
          value: col.id === "itRateOn" ? "" : "",
        })),
      },
    ]);

    // Focus on the first editable field (metal group) of the newly added row
    setTimeout(() => {
      const firstEditableIndex = COLUMNS.findIndex((col) => col.id !== "no");
      if (firstEditableIndex !== -1) {
        const firstInput =
          inputRefs.current[`${newRowIndex}-${firstEditableIndex}`];
        if (firstInput) {
          firstInput.focus();
        }
      }
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop =
          tableContainerRef.current.scrollHeight;
      }
      // Reset the flag after the row is added and focus is set
      window.isAddingRow = false;
    }, 150); // Increased timeout to ensure DOM is updated
  };

  // Function to focus on the first cell of the next row
  const focusFirstCellOfNextRow = (currentRowIndex) => {
    setTimeout(() => {
      const nextRowIndex = currentRowIndex + 1;
      if (nextRowIndex < tableData.length) {
        const firstCellInput = inputRefs.current[`${nextRowIndex}-0`];
        if (firstCellInput) firstCellInput.focus();
      }
    }, 0);
  };

  const handleDropdownClick = (rowIndex, colIndex, columnType) => {
    // Get position of the input element for positioning the dropdown
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }

    setActiveDropdown({ row: rowIndex, col: colIndex, type: columnType });
    setHighlightedIndex(0);
  };

  const handleDropdownSelect = (rowIndex, colIndex, selectedItem) => {
    const columnId = COLUMNS[colIndex]?.id;
    const value =
      typeof selectedItem === "object"
        ? selectedItem.name || selectedItem.label || ""
        : selectedItem;

    handleInputChange(rowIndex, colIndex, value, {
      productGroupId:
        typeof selectedItem === "object" ? selectedItem.value : undefined,
    });
    setActiveDropdown({ row: null, col: null, type: null });

    if (columnId === "metalGroup" && typeof selectedItem === "object") {
      productGroupOptionsCacheRef.current = mergeProductGroupOptions(
        productGroupOptionsCacheRef.current,
        [selectedItem],
      );
      applyPoolHeaderFromGroup(selectedItem);
    }

    // Calculate purity-based rate when metal group is selected
    // BUT: If userOverrodeItRate is true (loaded from edit), preserve original rate
    if (columnId === "metalGroup") {
      // Use the helper function to calculate and update itRate
      // The function will check userOverrodeItRate and preserve original value if set
      setTableData((prevData) => {
        const updatedData = calculateAndUpdateItRate(prevData, rowIndex);
        const purityCell = updatedData[rowIndex].data.find(
          (cell) => cell.id === "purity"
        );
        const selected = getMetalGroupOptionsList().find(
          (mg) =>
            mg.name === value ||
            mg.label === value ||
            String(mg.value) === String(selectedItem?.value)
        );
        if (purityCell && selected && selected.touch !== undefined) {
          purityCell.value = String(selected.touch || "");
        }
        return updatedData;
      });
    }

    // Focus on the next cell after selection
    setTimeout(() => {
      const nextColIndex = colIndex + 1;
      if (nextColIndex < COLUMNS.length) {
        const nextInput = inputRefs.current[`${rowIndex}-${nextColIndex}`];
        if (nextInput) nextInput.focus();
      } else {
        // If it's the last cell in the row, focus on the first cell of the next row
        focusFirstCellOfNextRow(rowIndex);
      }
    }, 0);
  };

  // Track the last processed tag number to prevent duplicate processing
  let lastProcessedTagNo = {
    value: null,
    timestamp: 0,
  };

  // Get filtered items based on input value and column type
  const getFilteredItems = (searchText, columnType) => {
    if (!dropdownData[columnType]) return [];

    // Normalize search text
    const normalizedSearchText = searchText
      ? String(searchText).toLowerCase()
      : "";

    // If no search, return all
    if (!normalizedSearchText) return dropdownData[columnType];

    // Special case: array of strings
    if (columnType === "itRateOn") {
      return dropdownData[columnType].filter((item) =>
        item.toLowerCase().includes(normalizedSearchText)
      );
    }

    // Special case: metalGroup — API search results (no client-side re-filter)
    if (columnType === "metalGroup") {
      return dropdownData[columnType] || [];
    }

    // Default handling
    return dropdownData[columnType].filter((item) => {
      if (typeof item === "object") {
        return item.label
          ? item.label.toLowerCase().includes(normalizedSearchText)
          : false;
      }
      return String(item).toLowerCase().includes(normalizedSearchText);
    });
  };

  // Add this function to get filtered items for Rate On dropdown
  const getFilteredRateOnItems = (searchText) => {
    if (!searchText) return rateOnOptions;
    return rateOnOptions.filter((item) =>
      item.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, rowIndex, colIndex, columnType) => {
    // Handle Tab key to move to next cell and potentially add a new row
    if (e.key === "Tab" && !e.shiftKey) {
      // If we're at the last column of the last row
      if (
        colIndex === COLUMNS.length - 1 &&
        rowIndex === tableData.length - 1
      ) {
        e.preventDefault();
        handleAddRow();
        // Focus will be set to the first cell of the new row after render
        setTimeout(() => {
          const newRowIndex = tableData.length;
          const firstCellInput = inputRefs.current[`${newRowIndex}-0`];
          if (firstCellInput) firstCellInput.focus();
        }, 0);
      }
    }

    // Handle dropdown keyboard navigation
    if (columnType === "rateOn" || dropdownColumns.includes(columnType)) {
      const cell = tableData[rowIndex]?.data[colIndex];
      if (!cell) return;

      const filtered =
        columnType === "rateOn"
          ? getFilteredRateOnItems(cell.value)
          : getFilteredItems(cell.value, columnType);

      if (filtered.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < filtered.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
          break;
        case "Enter":
          e.preventDefault();
          if (filtered[highlightedIndex]) {
            handleDropdownSelect(
              rowIndex,
              colIndex,
              filtered[highlightedIndex]
            );
          }
          break;
        case "Escape":
          e.preventDefault();
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        case "Tab":
          setActiveDropdown({ row: null, col: null, type: null });
          break;
        default:
          break;
      }
    }
  };

  // Render dropdown through portal
  const renderDropdown = () => {
    if (
      activeDropdown.row === null ||
      activeDropdown.col === null ||
      !activeDropdown.type
    )
      return null;

    const cell = tableData[activeDropdown.row]?.data[activeDropdown.col];
    if (!cell) return null;

    const filtered = getFilteredItems(cell.value, activeDropdown.type);
    // Special styling for metalGroup dropdown - match AddGirvi.jsx pattern
    const isMetalGroup = activeDropdown.type === "metalGroup";
    // For metalGroup: use 5 items with 40px height each (matching AddGirvi.jsx)
    // For others: use 5 items with 35px height each
    const VISIBLE_COUNT = 5;
    const ITEM_HEIGHT = isMetalGroup ? 40 : 35;
    const visibleItems = Math.min(filtered.length, VISIBLE_COUNT);
    const dropdownHeight = visibleItems * ITEM_HEIGHT;
    // Reset refs array
    dropdownItemsRef.current = [];

    return ReactDOM.createPortal(
      <div
        ref={dropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: isMetalGroup ? `${Math.max(200, dropdownPosition.width)}px` : `${dropdownPosition.width}px`,
          maxHeight: isMetalGroup ? `${dropdownHeight + 16}px` : `${dropdownHeight}px`,
          height: isMetalGroup ? `${dropdownHeight + 16}px` : `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
          boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY: filtered.length > VISIBLE_COUNT ? "auto" : "hidden",
            overflowX: "hidden",
          }}
        >
          {isMetalGroup && productGroupSearchLoading ? (
            <div
              className="dropdown-item text-muted"
              style={{ cursor: "default", padding: "0.4rem 0.75rem" }}
            >
              Searching...
            </div>
          ) : filtered.length > 0 ? (
            filtered.map((item, idx) => {
              // Handle both object and string types
              const itemLabel =
                typeof item === "object"
                  ? isMetalGroup
                    ? productGroupOptionDisplayName(item)
                    : item.name || item.label || ""
                  : item;
              const isSelected =
                typeof item === "object"
                  ? (item.label || "").toLowerCase() ===
                    (cell.value || "").toLowerCase()
                  : item.toLowerCase() === (cell.value || "").toLowerCase();

              return (
                <div
                  key={idx}
                  ref={(el) => (dropdownItemsRef.current[idx] = el)}
                  className={`dropdown-item ${
                    idx === highlightedIndex ? "active" : ""
                  }`}
                  title={itemLabel}
                  style={{
                    cursor: "pointer",
                    padding: "0.4rem 0.75rem",
                    fontSize: "0.85rem",
                    backgroundColor:
                      idx === highlightedIndex
                        ? "var(--theme-primary)"
                        : isSelected
                        ? "#f0f0f0"
                        : "transparent",
                    color: idx === highlightedIndex ? "#fff" : "inherit",
                    whiteSpace: isMetalGroup ? "normal" : "nowrap",
                    wordWrap: isMetalGroup ? "break-word" : "normal",
                    overflow: isMetalGroup ? "visible" : "hidden",
                    textOverflow: isMetalGroup ? "clip" : "ellipsis",
                  }}
                  onMouseEnter={() => {
                    setHighlightedIndex(idx);
                    if (isMetalGroup) applyPoolHeaderFromGroup(item);
                  }}
                  onMouseDown={() =>
                    handleDropdownSelect(
                      activeDropdown.row,
                      activeDropdown.col,
                      item
                    )
                  }
                >
                  {itemLabel}
                </div>
              );
            })
          ) : isMetalGroup && !metalGroupApiCalled ? null : (
            <div
              className="dropdown-item disabled"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                textAlign: "center",
              }}
            >
              No matching items
            </div>
          )}
        </div>
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Check if a column should have dropdown functionality
  const isDropdownColumn = (columnId) => {
    return dropdownColumns.includes(columnId);
  };

  // Check if a column is the image column
  const isImageColumn = (columnId) => {
    return columnId === "image";
  };

  // Get column type from index
  const getColumnType = (colIndex) => {
    return COLUMNS[colIndex]?.id;
  };

  // Add a function to handle focus on the last column
  const handleFocus = (rowIndex, colIndex) => {
    // Change background color
    const inputElement = inputRefs.current[`${rowIndex}-${colIndex}`];
    if (inputElement) {
      inputElement.style.backgroundColor = "#fff";
    }

    // If this is the last column of the last row, add a new row
    if (colIndex === COLUMNS.length - 1 && rowIndex === tableData.length - 1) {
      handleAddRow();
    }

    const columnType = getColumnType(colIndex);
    // If this is a dropdown column, show the dropdown
    // For non-dropdown columns (like Pcs), explicitly close any open dropdown
    if (isDropdownColumn(columnType)) {
      if (columnType === "metalGroup") {
        const metalGroupCell = tableData[rowIndex]?.data?.find(
          (cell) => cell.id === "metalGroup"
        );
        const metalGroupValue = (metalGroupCell?.value || "").toString().trim();
        const hasEnoughChars = metalGroupValue.length >= 3;
        if (!hasEnoughChars && metalGroupValue) {
          const selected = getMetalGroupOptionsList().find(
            (mg) =>
              mg.name === metalGroupValue ||
              mg.label === metalGroupValue ||
              (metalGroupCell?.productGroupId != null &&
                String(mg.value) === String(metalGroupCell.productGroupId)),
          );
          applyPoolHeaderFromGroup(selected);
        }
        const hasResults =
          getFilteredItems(metalGroupValue, "metalGroup").length > 0;

        // Only show dropdown if user has typed 3+ characters AND (has results OR API was called)
        if (hasEnoughChars && (hasResults || metalGroupApiCalled)) {
          handleDropdownClick(rowIndex, colIndex, columnType);
        }
        // Don't show dropdown if user just clicked/focused without typing 3+ characters
      } else {
        // For other dropdown columns, show dropdown normally
        handleDropdownClick(rowIndex, colIndex, columnType);
      }
    } else {
      // Non-dropdown column: ensure dropdown is closed
      setActiveDropdown({ row: null, col: null, type: null });
    }
  };

  // Modify the handleOtherAmountInteraction function
  const handleOtherAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openOtherAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        lastEnterPress.rowIndex === rowIndex &&
        now - lastEnterPress.time < 500
      ) {
        // 500ms threshold for double-Enter
        event.preventDefault();
        openOtherAmountModal(rowIndex);
        setLastEnterPress({ time: 0, rowIndex: null });
      } else {
        setLastEnterPress({ time: now, rowIndex });
      }
    } else if (event.type === "click") {
      // Show tooltip for 4 seconds on click
      const tooltipId = `oth-amt-tooltip-${rowIndex}`;
      const tooltip = tooltipRefs.current[tooltipId];

      if (tooltip) {
        // Clear any existing timeout
        if (tooltipTimeoutRef.current) {
          clearTimeout(tooltipTimeoutRef.current);
        }

        // Check if tooltip is already visible
        if (tooltip.style.display === "block") {
          // Reset the timeout to keep it visible
          tooltipTimeoutRef.current = setTimeout(() => {
            tooltip.style.display = "none";
            setActiveTooltip(null);
          }, 4000);
          return; // Exit to prevent further actions
        }

        tooltip.style.display = "block"; // Show tooltip immediately
        setActiveTooltip(tooltipId); // Set active tooltip

        // Set timeout to hide after 4 seconds
        tooltipTimeoutRef.current = setTimeout(() => {
          tooltip.style.display = "none";
          setActiveTooltip(null);
        }, 4000);
      }
    }
  };

  const handleTotalAmountInteraction = (rowIndex, event) => {
    if (event.type === "dblclick") {
      event.preventDefault();
      openTotalAmountModal(rowIndex);
    } else if (event.type === "keydown" && event.key === "Enter") {
      const now = Date.now();
      if (
        totalAmountEnterPress.rowIndex === rowIndex &&
        now - totalAmountEnterPress.time < 500
      ) {
        event.preventDefault();
        openTotalAmountModal(rowIndex);
        setTotalAmountEnterPress({ time: 0, rowIndex: null });
      } else {
        setTotalAmountEnterPress({ time: now, rowIndex });
      }
    }
  };

  // Trigger file input click
  const triggerFileInput = (rowIndex, colIndex) => {
    const fileInput = fileInputRefs.current[`${rowIndex}-${colIndex}`];
    if (fileInput) {
      fileInput.click();
    }
  };

  const openTotalAmountModal = (rowIndex) => {
    const row = tableData[rowIndex];
    if (!row) return;

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    const currentDetails = totalAmtCell?.totalAmountDetails;

    const currentAmount = currentDetails?.inclusiveAmount
      ? String(currentDetails.inclusiveAmount)
      : totalAmtCell &&
        totalAmtCell.value !== undefined &&
        totalAmtCell.value !== null
      ? String(totalAmtCell.value)
      : "";

    const currentGst = currentDetails?.gst ?? totalAmtCell?.gst ?? "3";

    setTotalAmountModal({
      isOpen: true,
      rowIndex,
      amount: currentAmount,
      gst: String(currentGst || "3"),
    });
  };

  const closeTotalAmountModal = (shouldRefocus = false) => {
    const { rowIndex } = totalAmountModal;

    setTotalAmountModal({
      isOpen: false,
      rowIndex: null,
      amount: "",
      gst: "3",
    });

    if (shouldRefocus && rowIndex !== null) {
      const totalAmtColIndex = COLUMNS.findIndex(
        (col) => col.id === "totalAmt"
      );
      if (totalAmtColIndex !== -1) {
        setTimeout(() => {
          const inputElement =
            inputRefs.current[`${rowIndex}-${totalAmtColIndex}`];
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 0);
      }
    }
  };

  const handleSaveTotalAmount = () => {
    const { rowIndex, amount, gst } = totalAmountModal;
    if (rowIndex === null) {
      closeTotalAmountModal();
      return;
    }

    const sanitizedAmount =
      typeof amount === "string" ? amount.trim() : String(amount || "");

    const gstPercentage = parseFloat(gst) || 0;
    const numericAmount = parseFloat(sanitizedAmount);
    const hasValidAmount = sanitizedAmount !== "" && !isNaN(numericAmount);

    const divisor = 1 + gstPercentage / 100;
    const baseAmount =
      hasValidAmount && divisor !== 0
        ? (numericAmount / divisor).toFixed(2)
        : "";
    const inclusiveFormatted = hasValidAmount ? numericAmount.toFixed(2) : "";

    setTableData((prevData) => {
      return prevData.map((row, idx) => {
        if (idx !== rowIndex) return row;

        const itAmtCell = row.data.find((cell) => cell.id === "itAmt");
        const othAmtCell = row.data.find((cell) => cell.id === "othAmt");
        const currentItAmt = parseFloat(itAmtCell?.value) || 0;
        const currentOthAmt = parseFloat(othAmtCell?.value) || 0;
        const manualBaseNumeric =
          hasValidAmount && baseAmount !== "" ? parseFloat(baseAmount) : null;

        const updatedRowData = row.data.map((cell) => {
          if (cell.id === "totalAmt") {
            const updatedCell = {
              ...cell,
            };

            if (!hasValidAmount) {
              updatedCell.value = "";
              if (updatedCell.totalAmountDetails) {
                delete updatedCell.totalAmountDetails;
              }
            } else {
              updatedCell.value = baseAmount;
              updatedCell.totalAmountDetails = {
                gst,
                inclusiveAmount: inclusiveFormatted,
                baseAmount,
                isManual: true,
              };
            }

            return updatedCell;
          }

          if (cell.id === "lbrAmt") {
            const updatedCell = {
              ...cell,
            };

            if (manualBaseNumeric !== null) {
              const manualLaborAmount = Math.max(
                manualBaseNumeric - (currentItAmt + currentOthAmt),
                0
              );
              updatedCell.value = manualLaborAmount.toFixed(2);
              updatedCell.isManual = true;
            } else if (updatedCell.isManual) {
              delete updatedCell.isManual;
            }

            return updatedCell;
          }

          return cell;
        });

        return {
          ...row,
          data: updatedRowData,
        };
      });
    });

    closeTotalAmountModal(true);
  };

  // Add helper function to open modal
  const openOtherAmountModal = (rowIndex) => {
    const currentEntries =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt")
        .otherAmountEntries || [];
    const currentValue =
      tableData[rowIndex].data.find((cell) => cell.id === "othAmt").value || "";

    // If there's a direct value but no entries, create an initial entry
    const initialEntries =
      currentEntries.length > 0
        ? currentEntries.map((entry) => ({
            ...entry,
            rateOn: entry.rateOn || "", // Ensure rateOn is initialized
          }))
        : currentValue
        ? [
            {
              chargeType: "Direct Entry",
              pcs: "",
              ct: "",
              wt: "",
              conPercentage: "",
              rateOn: "",
              rate: "",
              amount: currentValue,
            },
          ]
        : [];

    setOtherAmountModal({
      isOpen: true,
      rowIndex,
      entries: [...initialEntries],
      currentEntry: {
        chargeType: "",
        pcs: "",
        ct: "",
        wt: "",
        conPercentage: "",
        rateOn: "",
        rate: "",
        amount: "",
      },
      editingIndex: undefined,
    });
  };

  // Add function to save other amount entries
  const handleSaveOtherAmountEntries = () => {
    const { rowIndex, entries, currentEntry } = otherAmountModal;

    // If there's data in the current entry, validate and add it to entries
    let finalEntries = [...entries];
    if (currentEntry.amount && currentEntry.chargeType) {
      if (isNaN(currentEntry.amount)) {
        alert("Please enter a valid amount");
        return;
      }
      finalEntries.push({ ...currentEntry });
    }

    // Calculate total amount
    const totalAmount = finalEntries.reduce(
      (sum, entry) => sum + (parseFloat(entry.amount) || 0),
      0
    );

    // Calculate total less weight
    const totalLessWeight = finalEntries.reduce((sum, entry) => {
      const lessWeight = calculateLessWeight(entry);
      return sum + (isNaN(lessWeight) ? 0 : lessWeight);
    }, 0);

    // Update table data with both values
    setTableData((prev) => {
      const newData = [...prev];
      const row = newData[rowIndex];

      if (!row || !row.data) return prev;

      // Get all required values
      const grWtCell = row.data.find((cell) => cell.id === "grWt");
      // Removed lType and lbrPercentage references
      const lbrRateCell = row.data.find((cell) => cell.id === "lbrRate");
      const itRateCell = row.data.find((cell) => cell.id === "itRate");
      const itRateOnCell = row.data.find((cell) => cell.id === "itRateOn");
      const metalGroupCell = row.data.find((cell) => cell.id === "metalGroup");

      const grWt = parseFloat(grWtCell?.value) || 0;
      const lbrRate = parseFloat(lbrRateCell?.value) || 0;
      const itRate = parseFloat(itRateCell?.value) || 0;
      const itRateOn = (itRateOnCell?.value || "").toString().trim();
      const itRateOnUpper = itRateOn.toUpperCase(); // Normalize for comparison

      // Get metal type from metal group
      const selectedMetalGroup = getMetalGroupOptionsList().find(
        (mg) =>
          mg.label === metalGroupCell?.value ||
          mg.name === metalGroupCell?.value ||
          String(mg.value) === String(metalGroupCell?.value) ||
          (metalGroupCell?.productGroupId != null &&
            metalGroupCell.productGroupId !== "" &&
            String(mg.value) === String(metalGroupCell.productGroupId))
      );
      const metalType = selectedMetalGroup?.metal || "Gold";

      // Update Other Amount
      const othAmtCell = row.data.find((cell) => cell.id === "othAmt");
      if (othAmtCell) {
        othAmtCell.value = formatAmount(totalAmount);
        othAmtCell.otherAmountEntries = finalEntries;
      }

      // Update Less Weight
      const lsWtCell = row.data.find((cell) => cell.id === "lsWt");
      if (lsWtCell) {
        if (totalLessWeight > 0) {
          lsWtCell.value = formatLessWeight(totalLessWeight);
        }
      }

      // Calculate and update Net Weight
      const lwForNet =
        totalLessWeight > 0
          ? totalLessWeight
          : parseFloat(lsWtCell?.value) || 0;
      const netWeight = calculateNetWeight(grWt, lwForNet);
      const netWtCell = row.data.find((cell) => cell.id === "netWt");
      if (netWtCell) {
        netWtCell.value = netWeight.toFixed(2);
      }

      // Calculate and update Fine Weight
      const purityCell = row.data.find((cell) => cell.id === "purity");
      const wastageCell = row.data.find((cell) => cell.id === "wastage");
      const fineWtCell = row.data.find((cell) => cell.id === "fineWt");
      if (netWtCell && purityCell && wastageCell && fineWtCell) {
        const netWt = parseFloat(netWtCell.value) || 0;
        const purity = parseFloat(purityCell.value) || 0;
        const wastage = parseFloat(wastageCell.value) || 0;
        const fineWt = calculateFineWeight(netWt, purity, wastage);
        fineWtCell.value = fineWt.toFixed(2);
      }

      // Calculate Item Amount using selected ItRateOn base
      const itAmtCell = row.data.find((cell) => cell.id === "itAmt");
      if (itRateCell && itRate && itAmtCell && fineWtCell) {
        const fineWt = parseFloat(fineWtCell.value) || 0;
        const baseWeight =
          itRateOnUpper === "NWT"
            ? parseFloat(netWtCell?.value) || 0
            : itRateOnUpper === "GWT"
              ? parseFloat(grWtCell?.value) || 0
              : fineWt; // FWt (or empty)
        const itAmt = (baseWeight * itRate).toFixed(2);
        itAmtCell.value = itAmt;
      }

      // Calculate Labor Amount using fineWt and L.Rate
      const lbrAmtCell = row.data.find((cell) => cell.id === "lbrAmt");
      if (lbrAmtCell && !lbrAmtCell.isManual && fineWtCell) {
        const baseWeight = parseFloat(fineWtCell.value) || 0;
        let lbrAmt = 0;
        if (lbrRate > 0) {
          lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
        }
        lbrAmtCell.value = lbrAmt.toFixed(2);
      }

      // Calculate Total Amount
      const itAmt = parseFloat(itAmtCell?.value) || 0;
      const lbrAmt = parseFloat(lbrAmtCell?.value) || 0;
      const othAmt = parseFloat(othAmtCell?.value) || 0;
      const totalAmt = (itAmt + lbrAmt + othAmt).toFixed(2);

      const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
      if (totalAmtCell) {
        if (!totalAmtCell.totalAmountDetails?.isManual) {
          totalAmtCell.value = totalAmt;
          if (totalAmtCell.totalAmountDetails) {
            delete totalAmtCell.totalAmountDetails;
          }
        }
      }

      return newData;
    });

    // Close modal after saving
    setOtherAmountModal((prev) => ({
      ...prev,
      isOpen: false,
    }));
  };

  // Add function to handle column width changes
  const handleOtherAmountColumnWidthChange = (column, newWidth) => {
    setOtherAmountColumnWidths((prev) => ({
      ...prev,
      [column]: `${newWidth}px`,
    }));
  };

  const renderTotalAmountModal = () => {
    if (!totalAmountModal.isOpen) return null;

    return ReactDOM.createPortal(
      <Modal
        show={totalAmountModal.isOpen}
        onHide={() => closeTotalAmountModal(true)}
        centered
        size="sm"
      >
        <Modal.Header closeButton className="border-0 pb-0 align-items-center">
          <Modal.Title className="fw-semibold text-uppercase">
            GST Settlement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold mb-2">GST (%)</Form.Label>
            <div className="d-flex gap-3 flex-wrap">
              {["3", "5", "12", "18"].map((value) => (
                <Form.Check
                  key={value}
                  type="radio"
                  name="total-amount-gst-selection"
                  id={`total-amount-gst-${value}`}
                  label={`${value}%`}
                  value={value}
                  checked={totalAmountModal.gst === value}
                  onChange={(e) =>
                    setTotalAmountModal((prev) => ({
                      ...prev,
                      gst: e.target.value,
                    }))
                  }
                />
              ))}
            </div>
          </Form.Group>
          <Form.Group controlId="totalAmountModal-amount">
            <div className="d-flex gap-2">
              <Form.Control
                ref={totalAmountInputRef}
                type="text"
                value={totalAmountModal.amount}
                placeholder="Enter price"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    setTotalAmountModal((prev) => ({
                      ...prev,
                      amount: value,
                    }));
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveTotalAmount();
                  }
                }}
                style={{ flexBasis: "60%" }}
              />
              <Button
                variant="primary"
                className="px-4"
                onClick={handleSaveTotalAmount}
                style={{ flexBasis: "20%" }}
              >
                Save
              </Button>
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0"></Modal.Footer>
      </Modal>,
      document.getElementById("modal-portal")
    );
  };

  // Add this function right before the return statement in the Table component
  // Function to position tooltip based on screen size
  const positionTooltip = (targetElement, tooltip) => {
    const rect = targetElement.getBoundingClientRect();
    tooltip.style.display = "block";

    // Get arrow element
    const arrow = tooltip.querySelector(".tooltip-arrow");

    // Responsive positioning based on viewport
    const viewportWidth = window.innerWidth;

    if (viewportWidth < 576) {
      // Small screens (Bootstrap's sm breakpoint)
      // Position directly below the "i" button
      tooltip.style.left = `${rect.left + rect.width / 2 - 100}px`; // Center horizontally (tooltip width is 200px)
      tooltip.style.top = `${rect.bottom + 10}px`; // Position below the button
      tooltip.style.maxWidth = "calc(100vw - 20px)"; // 10px padding on each side

      // Position arrow at top center
      if (arrow) {
        arrow.style.top = "-6px"; // Adjusted for better alignment
        arrow.style.left = "50%";
        arrow.style.transform = "translateX(-50%) rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
        arrow.style.borderLeft = "1px solid rgba(0,0,0,0.1)";
        arrow.style.borderTop = "1px solid rgba(0,0,0,0.1)";
      }
    } else {
      // Position to the right on larger screens
      tooltip.style.left = `${rect.right + 10}px`;
      tooltip.style.top = `${rect.top - 5}px`;
      tooltip.style.maxWidth = "300px";

      // Position arrow at left middle
      if (arrow) {
        arrow.style.top = "15px";
        arrow.style.left = "-6px";
        arrow.style.transform = "rotate(45deg)";
        arrow.style.borderRight = "none";
        arrow.style.borderBottom = "none";
      }
    }
  };

  // Update the Rate On dropdown implementation
  const handleRateOnDropdown = (rowIndex, value, isCurrentEntry = false) => {
    if (isCurrentEntry) {
      setOtherAmountModal((prev) => {
        const updatedEntry = {
          ...prev.currentEntry,
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        return {
          ...prev,
          currentEntry: updatedEntry,
        };
      });
    } else {
      setOtherAmountModal((prev) => {
        const newEntries = [...prev.entries];
        const updatedEntry = {
          ...newEntries[rowIndex],
          rateOn: value,
        };
        // Calculate amount when rate on changes
        if (updatedEntry.rate) {
          updatedEntry.amount = formatAmount(calculateAmount(updatedEntry));
        }
        newEntries[rowIndex] = updatedEntry;
        return {
          ...prev,
          entries: newEntries,
        };
      });
    }

    // Hide the dropdown after selection
    const dropdowns = document.querySelectorAll(".dropdown-menu");
    dropdowns.forEach((dropdown) => {
      if (dropdown.style.display === "block") {
        dropdown.style.display = "none";
      }
    });
  };

  // Add keyboard navigation for Rate On dropdown
  const handleRateOnKeyDown = (e, rowIndex, isCurrentEntry = false) => {
    const currentValue = isCurrentEntry
      ? otherAmountModal.currentEntry.rateOn
      : otherAmountModal.entries[rowIndex].rateOn;

    const filtered = rateOnOptions.filter((option) =>
      option.toLowerCase().includes((currentValue || "").toLowerCase())
    );

    if (filtered.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (filtered[highlightedIndex]) {
          handleRateOnDropdown(
            rowIndex,
            filtered[highlightedIndex],
            isCurrentEntry
          );
        }
        break;
      case "Escape":
        e.preventDefault();
        // Close dropdown
        const dropdown = e.target.nextElementSibling;
        if (dropdown) {
          dropdown.style.display = "none";
        }
        break;
      default:
        break;
    }
  };


  const getRowEffectiveAmount = (row) => {
    if (!row || !row.data) return 0;

    const totalAmtCell = row.data.find((cell) => cell.id === "totalAmt");
    const totalAmtValue = totalAmtCell ? parseFloat(totalAmtCell.value) : NaN;

    return isNaN(totalAmtValue) ? 0 : totalAmtValue;
  };

  const calculateTotal = () => {
    if (!tableData) return 0;
    return tableData.reduce((sum, row) => {
      const effectiveAmount = getRowEffectiveAmount(row);
      return sum + effectiveAmount;
    }, 0);
  };

  // Add a helper function for number formatting
  const formatAmount = (value) => {
    if (!value || isNaN(value)) return "";
    // Convert to number and handle rounding based on decimal part
    const num = parseFloat(value);
    const decimalPart = num - Math.floor(num);
    // If decimal part is >= 0.5, round up, otherwise round down
    return decimalPart >= 0.5
      ? Math.ceil(num).toString()
      : Math.floor(num).toString();
  };

  // Update formatLessWeight to always return a value with 2 decimal places
  const formatLessWeight = (value) => {
    // Convert to number first
    const num = parseFloat(value);
    // Check if it's a valid number (including 0)
    if (isNaN(num)) return "0.00";
    // Always return with 2 decimal places
    return num.toFixed(2);
  };

  // Add this function to calculate Labor Amount
  const calculateLbrAmt = (baseWeight, lbrPercentage, lbrRate, metalType) => {
    let lbrAmt = 0;
    if (lbrPercentage > 0) {
      const masterRate =
        metalType === "Gold" ? masterGoldRate : masterSilverRate;
      lbrAmt = calculateLbrAmtWithPercentage(
        baseWeight,
        lbrPercentage,
        masterRate
      );
    } else if (lbrRate > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
    }
    return lbrAmt;
  };

  // Calculate total discount amount based on discount type and value
  const calculateDiscountAmount = () => {
    const totalAmount = calculateTotal();
    if (!discountValue || isNaN(discountValue) || totalAmount <= 0) {
      return 0;
    }
    const discount = parseFloat(discountValue);
    if (discountType === "%") {
      return (totalAmount * discount) / 100;
    } else {
      return discount;
    }
  };

  // Calculate GST amount based on percentage
  const calculateGstAmount = () => {
    const totalAmount = calculateTotal();
    const discountAmt = calculateDiscountAmount();
    const taxableAmount = totalAmount - discountAmt;

    if (!gstPercentage || isNaN(gstPercentage)) {
      return {
        total: 0,
        sgst: 0,
        cgst: 0,
        igst: 0,
      };
    }

    const gstRate = parseFloat(gstPercentage);
    const totalGstAmount = (taxableAmount * gstRate) / 100;

    // Get the business state (you may need to replace this with your actual business state)
    const businessState = "Gujarat"; // Replace with your actual business state

    // Check if customer state matches business state (case-insensitive comparison)
    const isIntraStateTransaction =
      customerDetails.state &&
      customerDetails.state.toLowerCase() === businessState.toLowerCase();

    if (isIntraStateTransaction) {
      // For intra-state transactions, split GST into SGST and CGST
      // SGST = (Taxable Amount × X%) ÷ 2
      // CGST = (Taxable Amount × X%) ÷ 2
      const halfGstRate = gstRate / 2;
      const sgstAmount = (taxableAmount * halfGstRate) / 100;
      const cgstAmount = (taxableAmount * halfGstRate) / 100;

      return {
        total: totalGstAmount,
        sgst: sgstAmount,
        cgst: cgstAmount,
        igst: 0,
      };
    } else {
      // For inter-state transactions, apply full GST as IGST
      return {
        total: totalGstAmount,
        sgst: 0,
        cgst: 0,
        igst: totalGstAmount,
      };
    }
  };

  // Amount before payment-section round-off (final - discount + GST).
  const calculateAmountBeforeRoundOff = () => {
    const totalAmount = calculateTotal();
    const discountAmt = calculateDiscountAmount();
    const gstAmt = calculateGstAmount().total;
    return totalAmount - discountAmt + gstAmt;
  };

  const getAutoPaymentRoundOff = () =>
    autoRoundOff(calculateAmountBeforeRoundOff()).roundOff;

  const getEffectiveRoundOff = () => {
    if (isManualRoundOff) {
      return parseFloat(roundOffAmount) || 0;
    }
    return getAutoPaymentRoundOff();
  };

  const getDisplayRoundOff = () => {
    if (isManualRoundOff) {
      return roundOffAmount;
    }
    return getAutoPaymentRoundOff().toFixed(2);
  };

  // Calculate grand total (total - discount + GST + round-off adjustment)
  const calculateGrandTotal = () => {
    const amountBeforeRoundOff = calculateAmountBeforeRoundOff();
    const roundOff = getEffectiveRoundOff();
    return (amountBeforeRoundOff + roundOff).toFixed(2);
  };

  const calculatePurchaseRemaining = () => {
    const grand = parseFloat(calculateGrandTotal()) || 0;
    const paid = purchasePaymentRows.reduce(
      (sum, r) => sum + (parseFloat(r.amount) || 0),
      0,
    );
    return Math.max(0, grand - paid).toFixed(2);
  };


  const calculatePurchaseTotalPaid = () => {
    return purchasePaymentRows.reduce(
      (sum, r) => sum + (parseFloat(r.amount) || 0),
      0,
    );
  };

  const calculatePurchasePayableAmount = () => {
    const totalPaid = calculatePurchaseTotalPaid();
    const grandTotal = parseFloat(calculateGrandTotal()) || 0;
    if (totalPaid > grandTotal) {
      return (totalPaid - grandTotal).toFixed(2);
    }
    return "0.00";
  };


  // Define product and setMetalGroups using useState

  // design / size removed from Purchase rows
  const [pcs, setPcs] = useState(0); // Initialize pcs state
  const [grWt, setGrWt] = useState(0); // Initialize grWt state
  const [lsWt, setLsWt] = useState(0); // Initialize lsWt state
  const [netWt, setNetWt] = useState(0); // Initialize netWt state
  const [lbrRate, setLbrRate] = useState(0); // Initialize lbrRate state
  const [lbrAmt, setLbrAmt] = useState(0); // Initialize lbramt state
  const [othamt, setOthAmt] = useState(0); // Initialize othAmt state
  const [No, setNo] = useState(0); // Initialize No state (assuming it's a number)
  const [metalGroup, setMetalGroup] = useState(""); // Initialize metalGroup state (assuming it's a string)
  const [showSuccessModal, setShowSuccessModal] = useState(false); // State for modal visibility
  const [discountType, setDiscountType] = useState("%"); // Discount type (% or ₹)
  const [discountValue, setDiscountValue] = useState(""); // Discount value
  const [roundOffAmount, setRoundOffAmount] = useState(""); // Change to string for input
  const [isManualRoundOff, setIsManualRoundOff] = useState(false);
  const [note, setNote] = useState(""); // Add note state

  const [followUpDate, setFollowUpDate] = useState("");

  // After-save navigation choice modal (Stock vs Purchase Table)
  const [showPostSaveModal, setShowPostSaveModal] = useState(false);

  // Keep purchaseId in sync with location state
  useEffect(() => {
    if (purchaseIdFromState) {
      setPurchaseId(purchaseIdFromState);
    } else {
      console.warn("No ID found in location state");
    }
  }, [purchaseIdFromState]);

  // Add resetForm function before handleSubmit
  const resetForm = () => {
    setBillDate(new Date().toISOString().split("T")[0]);

    // Reset customer fields
    setCustomerMobile("");
    setCustomerMobileSearchText("");
    setCustomerName("");
    setCustomerNameSearchText("");
    setCustomerAddress("");
    setCustomerCity("");
    setSelectedCustomerId(null);
    setCustomerDetails({
      email: "",
      birthDate: "",
      anniversaryDate: "",
      companyName: "",
      companyAddress: "",
      gstNumber: "",
      aadharNo: "",
      panNo: "",
      maritalStatus: "",
      // openingBalance: "",
      // pendingBalance: "",
      balance: "",
      saleBalance: "",
      reference: "",
      state: "",
    });

    // Reset table data
    setTableData([{ id: 1, data: initialRowData }]);
    setItemFieldErrors({});

    // Reset other fields
    setDiscountType("%");
    setDiscountValue("");
    setGstPercentage("");
    setRoundOffAmount("");
    setIsManualRoundOff(false);
    setNote("");
    setPurchasePaymentRows(defaultPurchasePaymentRows());
  };

  const handlePostSaveChoice = (goToStock) => {
    setShowPostSaveModal(false);
    if (goToStock) {
      navigate("/stocktable");
    } else {
      navigate("/purchasetable");
    }
  };

  // Keep idempotency_key stable across retries of the same submit attempt.
  const purchaseUpdateIdempotencyKeyRef = useRef(null);
  const purchaseUpdatePayloadFingerprintRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const itemErrors = collectLineItemFieldErrors(
        tableData,
        EDIT_PURCHASE_LINE_ERROR_OPTIONS,
      );
      const hasItemErrors = Object.keys(itemErrors).length > 0;
      setItemFieldErrors(hasItemErrors ? itemErrors : {});

      // Validate required fields
      // Validate party selection (Type / Name)
      if (!partyId) {
        toast.error("Please select a party (Name)");
        return;
      }

      // Format dates
      const formattedDate = billDate
        ? new Date(billDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];

      if (hasItemErrors) {
        return;
      }

      // Process purchase items
      const purchaseItems = [];
      for (let rowIndex = 0; rowIndex < tableData.length; rowIndex++) {
        const rowData = tableData[rowIndex].data.reduce((acc, cell) => {
          acc[cell.id] = cell.value;
          if (cell.id === "metalGroup" && cell.productGroupId != null) {
            acc.metalGroupId = cell.productGroupId;
          }
          if (cell.id === "totalAmt" && cell.totalAmountDetails?.gst) {
            acc.totalAmtGst = cell.totalAmountDetails.gst;
          }
          return acc;
        }, {});

        // Skip empty rows
        if (!rowData.metalGroup) {
          continue;
        }

        // Resolve product/metal group id (display may be "Name (id)" from API)
        let selectedMetalGroup = getMetalGroupOptionsList().find(
          (group) =>
            group.label === rowData.metalGroup ||
            group.name === rowData.metalGroup ||
            group.short_code === rowData.metalGroup ||
            String(group.value) === String(rowData.metalGroup)
        );
        if (
          !selectedMetalGroup &&
          rowData.metalGroupId != null &&
          rowData.metalGroupId !== ""
        ) {
          selectedMetalGroup = getMetalGroupOptionsList().find(
            (group) =>
              String(group.value) === String(rowData.metalGroupId)
          );
        }
        let resolvedMetalGroupId = selectedMetalGroup
          ? selectedMetalGroup.value
          : rowData.metalGroupId != null && rowData.metalGroupId !== ""
            ? Number(rowData.metalGroupId)
            : NaN;
        if (!Number.isFinite(Number(resolvedMetalGroupId))) {
          const idFromLabel = String(rowData.metalGroup || "").match(
            /\(([^)]+)\)\s*$/
          );
          if (idFromLabel) {
            resolvedMetalGroupId = Number(String(idFromLabel[1]).trim());
          }
        }
        if (!Number.isFinite(Number(resolvedMetalGroupId))) {
          throw new Error(`Invalid product group in row ${rowIndex + 1}`);
        }

        // Process charges
        const otherAmtCell = tableData[rowIndex].data.find(
          (cell) => cell.id === "othAmt"
        );
        const charges = mapOtherAmountEntriesForUpdate(
          otherAmtCell?.otherAmountEntries || [],
        );

        // Get purchase_item ID for editing
        const purchaseItemId = tableData[rowIndex].purchaseItemId || null;

        const lineMeta = tableData[rowIndex];
        const lineProductId =
          lineMeta?.lineProductId != null &&
          Number.isFinite(Number(lineMeta.lineProductId)) &&
          Number(lineMeta.lineProductId) > 0
            ? Number(lineMeta.lineProductId)
            : null;
        const lineTagNo = (lineMeta?.lineTagNo || "").trim();

        // Create purchase item (internal shape → mapped to API like AddPurchase)
        const purchaseItem = {
          ...(purchaseItemId ? { pur_it_id: purchaseItemId } : {}),
          metal_group: Number(resolvedMetalGroupId),
          product_id: lineProductId,
          tag_no: lineTagNo,
          pcs: Number(rowData.pcs),
          gwt: Number(rowData.grWt),
          lwt: Number(rowData.lsWt) || 0,
          nwt: Number(rowData.netWt),
          fineWt: Number(rowData.fineWt),
          itrateon: rowData.itRateOn
            ? String(rowData.itRateOn).trim()
            : "FWt",
          itrate: Number(rowData.itRate) || 0,
          itamt: Number(rowData.itAmt) || 0,
          purity: Number(rowData.purity) || 0,
          wastage: Number(rowData.wastage) || 0,
          lbr_rate: Number(rowData.lbrRate) || 0,
          lbr_amt: Number(rowData.lbrAmt) || 0,
          othamt: Number(rowData.othAmt) || 0,
          mrp: Number(rowData.mrp) || 0,
          image: rowData.image || "",
          totalamt: Number(rowData.totalAmt) || 0,
          charges: charges,
        };

        purchaseItems.push(purchaseItem);
      }

      if (purchaseItems.length === 0) {
        toast.error("No valid purchase entries to submit");
        return;
      }

      // Calculate total Pcs and total Net Wt for all rows
      const totalPcs = purchaseItems.reduce(
        (sum, item) => sum + (Number(item.pcs) || 0),
        0
      );
      const totalNetWt = purchaseItems.reduce(
        (sum, item) => sum + (Number(item.nwt) || 0),
        0
      );

      // Calculate amounts
      const gstAmts = calculateGstAmount();
      const taxAmount = Number(gstAmts.total) || 0;
      const grandTotal = Number(calculateGrandTotal()) || 0;

      if (!Number.isFinite(grandTotal) || grandTotal < 0) {
        toast.error("Grand total must be a valid number (≥ 0).");
        return;
      }

      // ERP update infers valued vs metal-only from numbers; enforce before submit.
      const lineMeetsMetalOnlyRules = (it) => {
        const rate = Number(it.itrate);
        const itemRateOk =
          it.itrate === "" ||
          it.itrate == null ||
          !Number.isFinite(rate) ||
          rate <= 0;
        const itamt = Number(it.itamt);
        const total = Number(it.totalamt);
        const amtOk =
          (!Number.isFinite(itamt) || itamt <= 0) &&
          (!Number.isFinite(total) || total <= 0);
        return itemRateOk && amtOk;
      };

      if (grandTotal > 0) {
        /* valued / bill purchase */
      } else if (grandTotal === 0) {
        if (taxAmount !== 0) {
          toast.error(
            "Metal-only purchase (₹0 total) requires GST/tax amount to be 0.",
          );
          return;
        }
        for (let i = 0; i < purchaseItems.length; i++) {
          if (!lineMeetsMetalOnlyRules(purchaseItems[i])) {
            toast.error(
              `Row ${i + 1}: For a ₹0 (metal-only) bill, line rate and amounts must be 0 or empty.`,
            );
            return;
          }
        }
      }

      // Validate that we have the required ID
      if (!purchaseId) {
        toast.error("Purchase ID is missing");
        return;
      }

      // Same modular item shape as AddPurchase create (+ line `id` when updating existing row)
      const apiItems = purchaseItems.map((it) => {
        const row = {
          product_group_id: Number(it.metal_group),
          pcs: Number(it.pcs) || 0,
          gwt: Number(it.gwt) || 0,
          lwt: Number(it.lwt) || 0,
          nwt: Number(it.nwt) || 0,
          purity: Number(it.purity) || 0,
          wastage: Number(it.wastage) || 0,
          fine_wt: Number(it.fineWt) || 0,
          item_rate_on: it.itrateon || "FWt",
          item_rate: Number(it.itrate) || 0,
          item_amount: Number(it.itamt) || 0,
          lbr_rate: Number(it.lbr_rate) || 0,
          lbr_amt: Number(it.lbr_amt) || 0,
          oth_amt: Number(it.othamt) || 0,
          mrp: Number(it.mrp) || 0,
          image: it.image || "",
          total_amount: Number(it.totalamt) || 0,
        };
        const pid = it.pur_it_id;
        if (pid != null && pid !== "" && Number.isFinite(Number(pid)) && Number(pid) > 0) {
          row.id = Number(pid);
        }
        if (it.product_id != null && Number(it.product_id) > 0) {
          row.product_id = Number(it.product_id);
        }
        const tag = (it.tag_no || "").trim();
        if (tag) row.tag_no = tag;
        row.charges = Array.isArray(it.charges) ? it.charges : [];
        return row;
      });

      const hasItemImages = tableData.some((row) =>
        row?.data?.some((cell) => cell?.id === "image" && cell?.imageFile)
      );
      if (hasItemImages) {
        toast("Line images are not uploaded with the new purchases API.", {
          icon: "ℹ️",
        });
      }

      // Metal-only (₹0 grand total): no money on the bill — payments must be empty.
      let paymentsPayload = [];
      const paymentPersistKeys = [];
      if (grandTotal > 0) {
        const paymentsSourceRows = purchasePaymentRows.map((r) => ({
          bank_account_id: Number(r.type) || 0,
          type: Number(r.type) || 0,
          amount: r.amount,
          paymentId: r.paymentId,
          paymentIdempotencyKey: r.paymentIdempotencyKey,
          pay_mode: r.pay_mode,
          remarks: r.remarks ?? "",
          rowKey: r.rowKey,
        }));

        for (let i = 0; i < purchasePaymentRows.length; i++) {
          const r = purchasePaymentRows[i];
          const amt = Number(r.amount) || 0;
          if (amt <= 0) continue;
          const bid = Number(r.type) || 0;
          if (!Number.isFinite(bid) || bid <= 0) {
            toast.error(
              `Payment row ${i + 1}: select a bank account when amount is greater than 0.`,
            );
            return;
          }
        }

        paymentsPayload = mapPurchasePaymentsForUpdateApi(paymentsSourceRows, {
          isUpdate: true,
          paymentTypes,
          createKey: createPurchasesIdempotencyKey,
          persistKeys: paymentPersistKeys,
        });

        if (paymentPersistKeys.length > 0) {
          setPurchasePaymentRows((prev) => {
            const next = [...prev];
            paymentPersistKeys.forEach(({ rowKey, key }) => {
              const idx = next.findIndex((r) => rowKey && r.rowKey === rowKey);
              if (idx >= 0) {
                next[idx] = { ...next[idx], paymentIdempotencyKey: key };
              }
            });
            return next;
          });
        }
      }

      const gstTotalNum = Number(gstAmts.total) || 0;
      const paidAmount = purchasePaymentRows.reduce(
        (sum, r) => sum + (parseFloat(r.amount) || 0),
        0,
      );
      const grandNum = Number(grandTotal) || 0;
      const remainingAmount = Math.max(0, grandNum - paidAmount);
      const gstPercentRaw =
        gstPercentage === "" || gstPercentage == null
          ? NaN
          : Number(gstPercentage);
      const gstPercent =
        Number.isFinite(gstPercentRaw) ? gstPercentRaw : null;

      const branchIdRaw = userData?.branch_id ?? userData?.branchId;
      const branch_id =
        branchIdRaw != null && branchIdRaw !== ""
          ? Number(branchIdRaw)
          : NaN;

      const updatePayloadForHash = {
        id: purchaseId,
        supplier_id: partyId,
        date: formattedDate,
        discount: Number(discountValue) || 0,
        tax_amount: gstTotalNum,
        gst_amount: gstTotalNum,
        gst_percent: gstPercent,
        round_off: getEffectiveRoundOff(),
        grand_total: grandNum,
        paid_amount: paidAmount,
        remaining_amount: remainingAmount,
        notes: note != null ? String(note) : "",
        items: apiItems,
        payments: paymentsPayload,
        ...(Number.isFinite(branch_id) && branch_id > 0
          ? { branch_id }
          : {}),
      };

      const fingerprint = JSON.stringify(updatePayloadForHash);
      if (
        !purchaseUpdateIdempotencyKeyRef.current ||
        purchaseUpdatePayloadFingerprintRef.current !== fingerprint
      ) {
        purchaseUpdateIdempotencyKeyRef.current =
          createPurchasesIdempotencyKey();
        purchaseUpdatePayloadFingerprintRef.current = fingerprint;
      }

      const response = await updatePurchase({
        ...updatePayloadForHash,
        idempotency_key: purchaseUpdateIdempotencyKeyRef.current,
      });

      if (isPurchasesApiSuccess(response)) {
        purchaseUpdateIdempotencyKeyRef.current = null;
        purchaseUpdatePayloadFingerprintRef.current = null;
        toast.success("Purchase updated successfully");
        navigate("/purchasetable");
      } else {
        console.error("API Error:", response?.message || response);
        toast.error(response?.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      console.error("Error details:", error.response?.data || error.message);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to submit the form. Please try again."
      );
    }
  };

  // New function to handle labor calculations
  const handleLaborCalculations = (tableData, rowIndex) => {
    const lbrRateCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrRate"
    );
    const lbrAmtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "lbrAmt"
    );
    if (lbrAmtCell?.isManual) {
      return;
    }
    // Use fineWt for labor calculations
    const fineWtCell = tableData[rowIndex].data.find(
      (cell) => cell.id === "fineWt"
    );
    const baseWeight = parseFloat(fineWtCell?.value) || 0;
    const lbrRate = parseFloat(lbrRateCell?.value) || 0;

    let lbrAmt = 0;
    if (lbrRate > 0 && baseWeight > 0) {
      lbrAmt = calculateLbrAmtWithRate(baseWeight, lbrRate);
    } else {
      lbrAmt = 0;
    }

    if (lbrAmtCell) {
      lbrAmtCell.value = Math.round(lbrAmt).toString();
      calculateTotalAmount(tableData, rowIndex);
    }
  };

  // Handle customer mobile input change
  const handleCustomerMobileChange = (e) => {
    const value = sanitizeMobileInput(e.target.value);
    setCustomerMobileSearchText(value);
    setCustomerMobile(value);
    setCustomerMobileDropdownActive(true);
    setCustomerMobileHighlightedIndex(0);
    setSelectedCustomerId(null);
    setIsNewCustomer(true);
  };

  // Get filtered users based on mobile number
  const getFilteredUserssByMobile = (searchText) => {
    if (!searchText) return userss;
    const searchLower = searchText.toLowerCase();
    return userss.filter((item) =>
      item.mobile.toLowerCase().includes(searchLower)
    );
  };

  // Function to handle customer mobile selection
  const handleCustomerMobileSelect = (selectedItem) => {
    setCustomerMobile(selectedItem.mobile);
    setCustomerMobileSearchText(selectedItem.mobile);
    setCustomerMobileDropdownActive(false);
    setSelectedCustomerId(selectedItem.id);
    setCustomerName(selectedItem.fullName);
    setCustomerNameSearchText(selectedItem.fullName);
    setCustomerAddress(selectedItem.address);
    setCustomerCity(selectedItem.city);
    setCustomerDetails({
      email: selectedItem.email || "",
      birthDate: selectedItem.birthDate || "",
      anniversaryDate: selectedItem.anniversaryDate || "",
      companyName: selectedItem.companyName || "",
      companyAddress: selectedItem.companyAddress || "",
      gstNumber: selectedItem.gstNumber || "",
      aadharNo: selectedItem.aadharNo || "",
      panNo: selectedItem.panNo || "",
      maritalStatus: selectedItem.maritalStatus || "",
      balance: selectedItem.balance || "",
      saleBalance: selectedItem.saleBalance || "",
      reference: selectedItem.reference || "",
      state: selectedItem.state || "",
    });
    setIsNewCustomer(false);
  };

  // Handle key navigation in customer mobile dropdown
  const handleCustomerMobileKeyDown = (e) => {
    const filtered = getFilteredUserssByMobile(customerMobileSearchText);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex;
          // Scroll the highlighted item into view
          if (customerMobileItemsRef.current[newIndex]) {
            customerMobileItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerMobileHighlightedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
          // Scroll the highlighted item into view
          if (customerMobileItemsRef.current[newIndex]) {
            customerMobileItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerMobileDropdownActive) {
          handleCustomerMobileSelect(filtered[customerMobileHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerMobileDropdownActive(false);
        break;
      case "Tab":
        setCustomerMobileDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Close customer mobile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerMobileDropdownRef.current &&
        !customerMobileDropdownRef.current.contains(event.target) &&
        customerMobileInputRef.current &&
        !customerMobileInputRef.current.contains(event.target)
      ) {
        setCustomerMobileDropdownActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render customer mobile dropdown
  const renderCustomerMobileDropdown = () => {
    if (!customerMobileDropdownActive) return null;

    const filtered = getFilteredUserssByMobile(customerMobileSearchText);

    // Reset refs array
    customerMobileItemsRef.current = [];

    // Calculate width based on input field
    const inputWidth = customerMobileInputRef.current
      ? customerMobileInputRef.current.offsetWidth
      : 150;
    const inputRect = customerMobileInputRef.current
      ? customerMobileInputRef.current.getBoundingClientRect()
      : null;

    if (!inputRect) return null;

    return ReactDOM.createPortal(
      <div
        ref={customerMobileDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${inputWidth}px`,
          maxHeight: "150px", // Reduced from 200px to show ~5 items
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
          fontSize: "12px",
          lineHeight: "1.2",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.mobile.toLowerCase() ===
              customerMobileSearchText.toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (customerMobileItemsRef.current[idx] = el)}
                className={`dropdown-item ${
                  idx === customerMobileHighlightedIndex ? "active" : ""
                }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerMobileHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                      ? "#f0f0f0"
                      : "transparent",
                  color:
                    idx === customerMobileHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "background-color 0.2s, color 0.2s", // Add transition for smooth highlight
                }}
                onMouseEnter={() => setCustomerMobileHighlightedIndex(idx)}
                onMouseDown={() => handleCustomerMobileSelect(item)}
              >
                {item.mobile} - {item.name}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching users</div>
        )}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Handle customer name input change
  const handleCustomerNameChange = (e) => {
    const value = e.target.value;
    setCustomerNameSearchText(value);
    setCustomerNameDropdownActive(true);
    setCustomerNameHighlightedIndex(0);
    setSelectedCustomerId(null);
    setIsNewCustomer(true);
  };

  // Get filtered users based on name
  const getFilteredUserssByName = (searchText) => {
    if (!searchText) return USERS;
    const searchLower = searchText.toLowerCase();
    return USERS.filter((item) =>
      item.name.toLowerCase().includes(searchLower)
    );
  };
  // Function to handle customer name selection
  const handleCustomerNameSelect = (selectedItem) => {
    setCustomerName(selectedItem.name);
    setCustomerNameSearchText(selectedItem.name);
    setCustomerNameDropdownActive(false);
    setSelectedCustomerId(selectedItem.id);

    // Auto-fill other customer fields
    setCustomerMobile(selectedItem.mobile);
    setCustomerMobileSearchText(selectedItem.mobile);
    setCustomerAddress(selectedItem.address);
    setCustomerCity(selectedItem.city);

    // Set all customer details
    setCustomerDetails({
      email: selectedItem.email || "",
      birthDate: selectedItem.birthDate || "",
      anniversaryDate: selectedItem.anniversaryDate || "",
      companyName: selectedItem.companyName || "",
      companyAddress: selectedItem.companyAddress || "",
      gstNumber: selectedItem.gstNumber || "",
      aadharNo: selectedItem.aadharNo || "",
      panNo: selectedItem.panNo || "",
      maritalStatus: selectedItem.maritalStatus || "",
      // openingBalance: selectedItem.openingBalance || "",
      // pendingBalance: selectedItem.pendingBalance || "",
      balance: selectedItem.balance || "",
      saleBalance: selectedItem.saleBalance || "",
      reference: selectedItem.reference || "",
      state: selectedItem.state || "",
    });

    setIsNewCustomer(false);
  };

  // Handle key navigation in customer name dropdown
  const handleCustomerNameKeyDown = (e) => {
    const filtered = getFilteredUserssByName(customerNameSearchText);

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setCustomerNameHighlightedIndex((prevIndex) => {
          const newIndex =
            prevIndex < filtered.length - 1 ? prevIndex + 1 : prevIndex;
          // Scroll the highlighted item into view
          if (customerNameItemsRef.current[newIndex]) {
            customerNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "ArrowUp":
        e.preventDefault();
        setCustomerNameHighlightedIndex((prevIndex) => {
          const newIndex = prevIndex > 0 ? prevIndex - 1 : 0;
          // Scroll the highlighted item into view
          if (customerNameItemsRef.current[newIndex]) {
            customerNameItemsRef.current[newIndex].scrollIntoView({
              block: "nearest",
              behavior: "smooth",
            });
          }
          return newIndex;
        });
        break;
      case "Enter":
        e.preventDefault();
        if (filtered.length > 0 && customerNameDropdownActive) {
          handleCustomerNameSelect(filtered[customerNameHighlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setCustomerNameDropdownActive(false);
        break;
      case "Tab":
        setCustomerNameDropdownActive(false);
        break;
      default:
        break;
    }
  };

  // Close customer name dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        customerNameDropdownRef.current &&
        !customerNameDropdownRef.current.contains(event.target) &&
        customerNameInputRef.current &&
        !customerNameInputRef.current.contains(event.target)
      ) {
        setCustomerNameDropdownActive(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Render customer name dropdown
  const renderCustomerNameDropdown = () => {
    if (!customerNameDropdownActive) return null;

    const filtered = getFilteredUserssByName(customerNameSearchText);

    // Reset refs array
    customerNameItemsRef.current = [];

    // Calculate width based on input field
    const inputWidth = customerNameInputRef.current
      ? customerNameInputRef.current.offsetWidth
      : 150;
    const inputRect = customerNameInputRef.current
      ? customerNameInputRef.current.getBoundingClientRect()
      : null;

    if (!inputRect) return null;

    return ReactDOM.createPortal(
      <div
        ref={customerNameDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${inputRect.bottom}px`,
          left: `${inputRect.left}px`,
          width: `${inputWidth}px`,
          minWidth: `${inputWidth}px`,
          maxWidth: `${inputWidth}px`,
          maxHeight: "150px", // Reduced from 200px to show ~5 items
          overflowY: "auto",
          zIndex: 9999,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          padding: "0.5rem 0",
          backgroundColor: "#fff",
          fontSize: "12px",
          lineHeight: "1.2",
        }}
      >
        {filtered.length > 0 ? (
          filtered.map((item, idx) => {
            const isSelected =
              item.name.toLowerCase() === customerNameSearchText.toLowerCase();

            return (
              <div
                key={idx}
                ref={(el) => (customerNameItemsRef.current[idx] = el)}
                className={`dropdown-item ${
                  idx === customerNameHighlightedIndex ? "active" : ""
                }`}
                style={{
                  cursor: "pointer",
                  padding: "0.25rem 1rem",
                  fontSize: "0.85rem",
                  backgroundColor:
                    idx === customerNameHighlightedIndex
                      ? "var(--theme-primary)"
                      : isSelected
                      ? "#f0f0f0"
                      : "transparent",
                  color:
                    idx === customerNameHighlightedIndex ? "#fff" : "inherit",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  transition: "background-color 0.2s, color 0.2s", // Add transition for smooth highlight
                }}
                onMouseEnter={() => setCustomerNameHighlightedIndex(idx)}
                onMouseDown={() => handleCustomerNameSelect(item)}
              >
                {item.name} - {item.mobile}
              </div>
            );
          })
        ) : (
          <div className="dropdown-item disabled">No matching users</div>
        )}
      </div>,
      document.getElementById("dropdown-portal")
    );
  };

  // Handle customer address change
  const handleCustomerAddressChange = (e) => {
    setCustomerAddress(e.target.value);
  };

  // Handle customer city change
  const handleCustomerCityChange = (e) => {
    setCustomerCity(e.target.value);
  };

  // Function to save a new customer
  const handleSaveCustomer = async () => {
    if (!customerMobileSearchText || !customerNameSearchText) {
      toast.error("Mobile number and name are required");
      return;
    }

    try {
      const response = await createCustomerUser({
        mobile: customerMobileSearchText.trim(),
        name: customerNameSearchText.trim(),
        address: customerAddress || undefined,
        city: customerCity || undefined,
        state: customerDetails.state || undefined,
      });

      if (isErpApiSuccess(response)) {
        toast.success("Customer created successfully");

        // Reset fields after success
        setCustomerMobile("");
        setCustomerMobileSearchText("");
        setCustomerName("");
        setCustomerNameSearchText("");
        setCustomerAddress("");
        setCustomerCity("");
        setCustomerDetails((prev) => ({ ...prev, state: "" }));

        setIsNewCustomer(false);
        fetchUserss(); // Refresh the list with new data
      } else {
        toast.error(response?.message || "Failed to save customer");
      }
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error("Error saving customer");
    }
  };

  // Add CSS to remove number input spinners
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      /* Remove number input spinners */
      input[type="number"]::-webkit-inner-spin-button,
      input[type="number"]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type="number"] {
        -moz-appearance: textfield;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Handle customer state change
  const handleCustomerStateChange = (e) => {
    setCustomerDetails({
      ...customerDetails,
      state: e.target.value,
    });
  };

  // Handle round-off amount change (any edit locks manual mode; empty = no round off)
  const handleRoundOffChange = (e) => {
    handleRoundOffInputChange(
      e,
      (value) => {
        setIsManualRoundOff(true);
        setRoundOffAmount(value);
      },
      {
        allowNegative: true,
        simple: true,
      },
    );
  };

  // Handle note change
  const handleNoteChange = (e) => {
    const value = e.target.value;
    if (value.length <= 70) {
      setNote(value);
    }
  };

  const handleDeleteRow = (rowIndex) => {
    setTableData((prevData) => {
      const newData = prevData.filter((_, index) => index !== rowIndex);
      return newData;
    });
  };

  const location = useLocation();
  const customOrderId = location.state?.customOrderId;
  const customerUserId = location.state?.customerUserId;
  const repairingOrderId = location.state?.repairingOrderId;
  const isFromRepairingOrder = location.state?.isFromRepairingOrder;
  const [hasFilledFromCustomOrder, setHasFilledFromCustomOrder] =
    useState(false);
  const [customOrderData, setCustomOrderData] = useState(null);
  const [isLoadingOrderData, setIsLoadingOrderData] = useState(false);

  // Fetch order data using show_order_queue API
  const fetchOrderData = async (orderId) => {
    if (!orderId) return;

    setIsLoadingOrderData(true);
    try {
      const response = await showOrderQueue({});
      const d = response?.data;
      let orders = [];
      if (isErpApiSuccess(response)) {
        if (Array.isArray(d)) orders = d;
        else if (Array.isArray(d?.data)) orders = d.data;
        else if (Array.isArray(d?.orders)) orders = d.orders;
        else if (Array.isArray(d?.records)) orders = d.records;
      }

      const foundOrder = orders.find(
        (order) => String(order.id) === String(orderId)
      );
      if (foundOrder) {
        setCustomOrderData(foundOrder);

        // Set customer name and mobile from the order's user_id
        if (foundOrder.user_id && userss && userss.length > 0) {
          const customer = userss.find(
            (user) => String(user.id) === String(foundOrder.user_id)
          );
          if (customer) {
            setCustomerName(customer.fullName || customer.name || "");
            setCustomerNameSearchText(customer.fullName || customer.name || "");
            setCustomerMobile(customer.mobile || "");
            setCustomerMobileSearchText(customer.mobile || "");
            setSelectedCustomerId(customer.id);
            setCustomerCity(customer.city || "");
            setCustomerAddress(customer.address || "");
            setCustomerDetails((prevDetails) => ({
              ...prevDetails,
              email: customer.email || "",
              birthDate: customer.birthDate || "",
              anniversaryDate: customer.anniversaryDate || "",
              companyName: customer.companyName || "",
              companyAddress: customer.companyAddress || "",
              gstNumber: customer.gstNumber || "",
              aadharNo: customer.aadharNo || "",
              panNo: customer.panNo || "",
              maritalStatus: customer.maritalStatus || "",
              balance: customer.balance || "",
              saleBalance: customer.saleBalance || "",
              reference: customer.reference || "",
              state: customer.state || "",
            }));
          }
        }

        // Fetch products from order queue
        await fetchProductsFromOrderQueue(foundOrder.products || []);
      } else {
        console.error("Order not found with ID:", orderId);
        toast.error("Order not found");
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
      toast.error("Failed to fetch order data");
    } finally {
      setIsLoadingOrderData(false);
    }
  };

  // Fetch product details for order products using show_pro API with type "all"
  const fetchProductsFromOrderQueue = async (orderProducts) => {
    try {
      const ids = orderProducts.map((p) => p.pro_id).filter((x) => x != null && x !== "");
      if (!ids.length) {
        toast.error("No product IDs in order");
        return;
      }
      const response = await showProducts({ id: ids });
      const allProducts = normalizeProductStockShowResponse(response);
      if (isErpApiSuccess(response) && response.data) {
        if (allProducts.length === 0) {
          console.warn("No products found in response");
          toast.error("No product details found");
          return;
        }
        setAllProducts(allProducts);

        // Process each product in the order
        const processedProducts = orderProducts
          .map((orderProduct, index) => {
            const product = allProducts.find(
              (p) => String(p.id) === String(orderProduct.pro_id)
            );
            if (product) {
              return {
                id: index + 1,
                data: COLUMNS.map((col) => {
                  let value = "";
                  let extra = {};
                  switch (col.id) {
                    case "metalGroup": {
                      const pgName =
                        product.product_group_name ??
                        product.metal_group_name ??
                        "";
                      const rawId =
                        product.product_group_id ??
                        product.product_group ??
                        product.metal_group ??
                        product.metal_group_id ??
                        "";
                      value = formatPurchaseEditProductGroupDisplay(pgName, rawId);
                      const numId = Number(rawId);
                      extra.productGroupId =
                        rawId !== "" && rawId != null
                          ? Number.isFinite(numId)
                            ? numId
                            : rawId
                          : null;
                      break;
                    }
                    case "pcs":
                      value = product.pcs || "";
                      break;
                    case "purity":
                      value = product.purity || "";
                      break;
                    case "wastage":
                      value = product.wastage || "";
                      break;
                    case "grWt":
                      value = product.GWT || "";
                      break;
                    case "lsWt":
                      value = product.LWT || "";
                      break;
                    case "netWt":
                      value = product.NWT || "";
                      break;
                    case "itRateOn": {
                      const raw =
                        product.itrateon ||
                        product.itRateOn ||
                        product.it_rate_on ||
                        product.item_rate_on ||
                        "";
                      value = mapPurchaseItemRateOnApiToUi(raw);
                      break;
                    }
                    case "fineWt":
                      // Calculate fineWt from netWt, purity, and wastage
                      const netWt = parseFloat(product.NWT) || 0;
                      const purity = parseFloat(product.purity) || 0;
                      const wastage = parseFloat(product.wastage) || 0;
                      value = calculateFineWeight(
                        netWt,
                        purity,
                        wastage
                      ).toFixed(2);
                      break;
                    case "itRate":
                      value = product.itrate || "";
                      break;
                    case "itAmt":
                      value = product.itamt || "";
                      break;
                    case "lType":
                      value = product.LBR_type || "";
                      break;
                    case "lbrPercentage":
                      value = product.LBR || "";
                      break;
                    case "lbrRate":
                      value = product.LBR_rate || "";
                      break;
                    case "lbrAmt":
                      value = product.LBR_amt || "";
                      break;
                    case "othAmt":
                      value = product.othamt || "";
                      extra.otherAmountEntries = mapApiChargesToOtherAmountEntries(
                        product.charges,
                      );
                      break;
                    case "mrp":
                      value = product.mrp || "";
                      break;
                    case "image":
                      value = product.image || "";
                      break;
                    case "totalAmt":
                      value = product.totalamt || "";
                      break;
                    default:
                      value = "";
                  }
                  if (col.id === "othAmt") {
                    return { id: col.id, value, ...extra };
                  }
                  if (col.id === "image") {
                    return {
                      id: col.id,
                      value,
                      imageFile: null,
                      imagePreview: value ? getNodeImageUrl(value) : null,
                    };
                  }
                  return { id: col.id, value, ...extra };
                }),
              };
            } else {
              return null;
            }
          })
          .filter(Boolean); // Remove null entries

        if (processedProducts.length > 0) {
          setTableData(processedProducts);
        }
      } else {
        console.error("Unexpected product data format:", response.data);
      }
    } catch (error) {
      console.error("Error fetching product details for order:", error);
      toast.error("Failed to fetch product details");
    }
  };

  const hasLoadedPurchaseRef = useRef(false);
  // Load existing purchase details and prefill the form
  useEffect(() => {
    if (hasLoadedPurchaseRef.current) {
      return;
    }
    hasLoadedPurchaseRef.current = true;
    const loadPurchaseForEdit = async () => {
      try {
        if (!purchaseId) {
          return;
        }
        // Bill type is derived from the purchase show payload.
        const res = await showPurchase({ id: purchaseId });

        if (!isPurchasesApiSuccess(res)) {
          console.error("Invalid API response:", res);
          toast.error("Failed to load purchase: Invalid response");
          return;
        }

        const purchase =
          getLegacyPurchaseDetailsFromShowResponse(res) || null;

        if (!purchase) {
          console.error("Purchase object is null");
          toast.error("Purchase data not found");
          return;
        }

        // Store purchase_details_id (pur_de_id)
        if (purchase.id) {
          setPurDeId(purchase.id);
        }

        // Date
        if (purchase.bill_date) {
          setBillDate(purchase.bill_date);
        }

        // Totals / header fields from payload
        setDiscountValue(
          purchase.discount_amount !== undefined
            ? String(purchase.discount_amount)
            : ""
        );
        if (purchase.gst_percentage !== undefined) {
          setGstPercentage(String(purchase.gst_percentage));
        }
        if (purchase.other_charges !== undefined) {
          setOtherChargesValue(String(purchase.other_charges));
        }
        if (purchase.round_off !== undefined) {
          setRoundOffAmount(String(purchase.round_off));
          setIsManualRoundOff(true);
        }
        if (purchase.remarks) {
          setNote(purchase.remarks);
        } else if (purchase.note) {
          setNote(purchase.note);
        }

        // Party (Type / Name / Mobile) - use karigar fields from payload
        setPartyId(purchase.ref_id || "");
        const displayName =
          purchase.karigar_company_name ||
          purchase.karigar_name ||
          String(purchase.ref_id || "");
        setPartyName(displayName);
        setPartyMobile(purchase.karigar_contact_number || "");

        // Items
        const items = Array.isArray(purchase.purchase_items)
          ? purchase.purchase_items
          : [];
        const fromApi = mapShowPaymentsToPurchasePaymentRows(
          purchase.payments,
        );
        setPurchasePaymentRows(
          fromApi && fromApi.length > 0 ? fromApi : defaultPurchasePaymentRows(),
        );

        if (items.length > 0) {
          const newTableData = items.map((item, index) => ({
            id: index + 1,
            purchaseItemId: item.id || null, // Store purchase_item ID for updates
            lineProductId:
              item.product_id != null && item.product_id !== ""
                ? Number(item.product_id)
                : null,
            lineTagNo: item.tag_no ? String(item.tag_no).trim() : "",
            userOverrodeItRate: true, // Mark as overridden to preserve original saved rates
            data: COLUMNS.map((col) => {
              let value = "";
              let extra = {};
              switch (col.id) {
                case "metalGroup": {
                  const pgName =
                    item.metal_group_name ?? item.product_group_name ?? "";
                  const rawId =
                    item.metal_group ??
                    item.product_group_id ??
                    item.metal_group_id ??
                    "";
                  value = formatPurchaseEditProductGroupDisplay(pgName, rawId);
                  const numId = Number(rawId);
                  extra.productGroupId =
                    rawId !== "" && rawId !== null && rawId !== undefined
                      ? Number.isFinite(numId)
                        ? numId
                        : rawId
                      : null;
                  break;
                }
                case "pcs":
                  value = item.pcs || "";
                  break;
                case "purity":
                  value = item.Purity || item.purity || "";
                  break;
                case "wastage":
                  value = item.Wastage || item.wastage || "";
                  break;
                case "grWt":
                  value = item.gwt || item.gwt || "";
                  break;
                case "lsWt":
                  value = item.lwt || "";
                  break;
                case "netWt":
                  value = item.nwt || "";
                  break;
                case "fineWt":
                  value = item.fineWt || "";
                  // If fineWt is not in API, calculate it from netWt, purity, and wastage
                  if (!value) {
                    const netWt = parseFloat(item.nwt) || 0;
                    const purity = parseFloat(item.Purity || item.purity) || 0;
                    const wastage =
                      parseFloat(item.Wastage || item.wastage) || 0;
                    value = calculateFineWeight(netWt, purity, wastage).toFixed(
                      2
                    );
                  }
                  break;
                case "itRateOn": {
                  const raw =
                    item.itrateon ||
                    item.itRateOn ||
                    item.it_rate_on ||
                    item.item_rate_on ||
                    "";
                  value = mapPurchaseItemRateOnApiToUi(raw);
                  break;
                }
                case "itRate":
                  value = item.itrate || "";
                  break;
                case "itAmt":
                  value = item.itamt || "";
                  break;
                case "lType":
                  value = item.lbr_type || "";
                  break;
                case "lbrPercentage":
                  value = item.lbr_percent || "";
                  break;
                case "lbrRate":
                  value = item.lbr_rate || "";
                  break;
                case "lbrAmt":
                  value = item.lbr_amt || "";
                  break;
                case "othAmt":
                  value = item.othamt || "";
                  extra.otherAmountEntries = mapApiChargesToOtherAmountEntries(
                    item.charges,
                  );
                  break;
                case "mrp":
                  value = item.mrp || "";
                  break;
                case "image":
                  value = item.image || "";
                  break;
                case "totalAmt":
                  value = item.totalamt || "";
                  break;
                default:
                  value = "";
              }
              if (col.id === "image") {
                return {
                  id: col.id,
                  value,
                  imageFile: null,
                  imagePreview: value ? getNodeImageUrl(value) : null,
                  ...extra,
                };
              }
              return { id: col.id, value, ...extra };
            }),
          }));
          // Set table data - this preserves original saved values
          // The userOverrodeItRate flag prevents recalculation with live rates
          setTableData(newTableData);
        }
      } catch (error) {
        console.error("Error loading purchase for edit:", error);
        toast.error(
          "Failed to load purchase data: " +
            (error.response?.data?.message || error.message)
        );
      }
    };

    loadPurchaseForEdit();
  }, [purchaseId]);

  // Fetch order data when only customOrderId is provided
  useEffect(() => {
    if (
      customOrderId &&
      !location.state?.customOrderData &&
      userss &&
      userss.length > 0
    ) {
      fetchOrderData(customOrderId);
    }
  }, [customOrderId, userss, location.state?.customOrderData]);

  // Recalculate It Rate from live price when master rates change
  // DISABLED for EditPurchase - we want to preserve original saved values
  // useEffect(() => {
  //   if (!tableData || tableData.length === 0) return;
  //   setTableData((prev) => {
  //     let updated = [...prev];
  //     for (let i = 0; i < updated.length; i++) {
  //       const mgCell = updated[i].data.find((c) => c.id === "metalGroup");
  //       if (!mgCell || !mgCell.value) continue;
  //       updated = calculateAndUpdateItRate(updated, i);
  //     }
  //     return updated;
  //   });
  // }, [masterGoldRate, masterSilverRate]);

  // Replace the old useEffect with the custom hook
  useFillSalesFromCustomOrder({
    locationState: customOrderData
      ? {
          customOrderId: customOrderId,
          customOrderTable: customOrderData.products || [],
          customOrderData: customOrderData,
          customOrderPayments: customOrderData.payments || [],
        }
      : location.state,
    setTableData,
    setPaymentRows: () => {}, // No-op function since payment table is removed
    setRoundOffAmount: (value) => {
      setRoundOffAmount(value);
      setIsManualRoundOff(true);
    },
    setNote,
    COLUMNS,
    dropdownData,
    hasFilledFromCustomOrder,
    setHasFilledFromCustomOrder,
    userss,
    setCustomerName,
    setCustomerNameSearchText,
    setCustomerMobile,
    setCustomerMobileSearchText,
    setSelectedCustomerId,
    setCustomerDetails,
    setCustomerCity,
    setCustomerAddress,
    setSelectedState,
    states,
    cities,
    allProducts,
    productGroups: getMetalGroupOptionsList(),
  });

  // Pre-fill customer data when customerUserId is provided
  useEffect(() => {
    if (customerUserId && userss && userss.length > 0) {
      const customerUser = userss.find(
        (user) => String(user.id) === String(customerUserId)
      );
      if (customerUser) {
        setCustomerName(customerUser.name || customerUser.fullName || "");
        setCustomerNameSearchText(
          customerUser.name || customerUser.fullName || ""
        );
        setCustomerMobile(customerUser.mobile || "");
        setCustomerMobileSearchText(customerUser.mobile || "");
        setSelectedCustomerId(customerUser.id);
        setCustomerCity(customerUser.city || "");
        setCustomerAddress(customerUser.address || "");
        // Set additional customer details
        setCustomerDetails((prevDetails) => ({
          ...prevDetails,
          email: customerUser.email || "",
          birthDate: customerUser.birthDate || "",
          anniversaryDate: customerUser.anniversaryDate || "",
          companyName: customerUser.companyName || "",
          companyAddress: customerUser.companyAddress || "",
          gstNumber: customerUser.gstNumber || "",
          aadharNo: customerUser.aadharNo || "",
          panNo: customerUser.panNo || "",
          maritalStatus: customerUser.maritalStatus || "",
          balance: customerUser.balance || "",
          saleBalance: customerUser.saleBalance || "",
          reference: customerUser.reference || "",
          state: customerUser.state || "",
        }));
        // Set state if available
        if (customerUser.state) {
          setSelectedState(customerUser.state);
        }
      }
    }
  }, [customerUserId, userss]);

  return (
    <div className={`body-wrapper ${cardClass}`}>
      <div className="container-fluid">
        <div className={`card shadow-sm ${cardClass}`}>
          <div className="px-4 py-3 border-bottom">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 w-100">
              <h4 className="card-title mb-0">Edit Purchase</h4>
              {(selectedCustomerId || productGroupPoolHeader) && (
                <div className="d-flex align-items-center flex-wrap gap-2 ms-auto">
                  {selectedCustomerId && (
                    <>
                      <span
                        className={`badge ${
                          parseFloat(customerDetails.balance) >= 0
                            ? "bg-success-subtle text-success"
                            : "bg-danger-subtle text-danger"
                        }`}
                        style={{ fontSize: "0.85rem" }}
                      >
                        Balance: ₹
                        {parseFloat(customerDetails.balance || 0).toFixed(2)}
                      </span>
                      <span
                        className={`badge ${
                          parseFloat(customerDetails.saleBalance) > 0
                            ? "bg-danger-subtle text-danger"
                            : "bg-success-subtle text-success"
                        } ms-2`}
                        style={{ fontSize: "0.85rem" }}
                      >
                        Pending: ₹
                        {parseFloat(customerDetails.saleBalance || 0).toFixed(2)}
                      </span>
                    </>
                  )}
                  <ProductGroupPoolBalance
                    balance={productGroupPoolHeader}
                    className=""
                  />
                </div>
              )}
            </div>
          </div>
          <div className="card-body">
            <div className="row mb-1">
              {/* Date Component */}
              <div className="col-lg-2 col-md-4 col-sm-6 mb-2">
                <div className="d-flex align-items-center">
                  <label
                    className="form-label me-2 mb-0"
                    style={{
                      width: "50px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                    }}
                  >
                    Date:
                  </label>
                  <input
                    type="date"
                    className={`form-control form-control-sm ${buttonClass}`}
                    value={billDate}
                    onChange={handleDateChange}
                    style={{ fontSize: "0.85rem" }}
                  />
                </div>
              </div>

              {/* Party Name (search, 3+ chars) */}
              <div className="col-lg-3 col-md-5 col-sm-6 mb-2">
                <div className="d-flex align-items-center position-relative">
                  <label
                    className="form-label me-1 mb-0 fw-semibold text-nowrap"
                    style={{ minWidth: "55px" }}
                  >
                    Name:
                  </label>
                  <div className="position-relative flex-grow-1">
                    <input
                      ref={partyNameInputRef}
                      type="text"
                      className={`form-control form-control-sm ${buttonClass}`}
                      placeholder="Name"
                      value={partyName}
                      onChange={handlePartyNameChange}
                      onFocus={handlePartyNameFocus}
                      onBlur={handlePartyNameBlur}
                      onKeyDown={handlePartyNameKeyDown}
                      autoComplete="off"
                    />
                  </div>
                </div>
              </div>

              {/* Party Mobile (read only) */}
              <div className="col-lg-3 col-md-7 col-sm-12 mb-2">
                <div className="d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className={`form-control form-control-sm ${buttonClass}`}
                    placeholder="Mobile No"
                    value={partyMobile}
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div
              ref={tableContainerRef}
              className={`table-responsive border ${
                buttonTableStyle === "square" ? "rounded-0" : "rounded-2"
              } mt-3 ${cardClass}`}
              style={{
                maxHeight:
                  tableData.length > 6
                    ? `${40 + 6 * 38 + 38}px`
                    : undefined,
                overflowY: tableData.length > 6 ? "auto" : "visible",
              }}
            >
              {/* No table-rounded — overflow:hidden breaks sticky header/total */}
              <table className="table table-bordered table-hover table-sm mb-0 theme-header-table">
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.id}
                        className="px-2 py-2 text-center"
                        style={{
                          fontSize: "0.85rem",
                          width: column.width,
                          fontWeight: "600",
                          whiteSpace: "nowrap",
                          position: tableData.length > 6 ? "sticky" : undefined,
                          top: tableData.length > 6 ? 0 : undefined,
                          zIndex: tableData.length > 6 ? 5 : undefined,
                        }}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                      {row.data.map((cell, colIndex) => {
                        // Add special handling for serial number column
                        if (cell.id === "no") {
                          return (
                            <td
                              key={cell.id}
                              className="p-0"
                              style={{ width: COLUMNS[colIndex]?.width }}
                            >
                              <div
                                className="d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <span
                                  style={{
                                    fontSize: "0.85rem",
                                    cursor: "pointer",
                                    transition: "opacity 0.2s",
                                  }}
                                  onClick={() => handleDeleteRow(rowIndex)}
                                  onMouseEnter={(e) => {
                                    e.target.textContent = "×";
                                    e.target.style.fontWeight = "600";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.textContent = rowIndex + 1;
                                    e.target.style.fontWeight = "normal";
                                  }}
                                >
                                  {rowIndex + 1}
                                </span>
                              </div>
                            </td>
                          );
                        }

                        // Check if this is the LWt column and if there are Other Amount entries
                        const hasOtherAmountEntries =
                          row.data.find((c) => c.id === "othAmt")
                            ?.otherAmountEntries?.length > 0;
                        const hasFieldError = !!itemFieldErrors[row.id]?.includes(
                          cell.id,
                        );

                        return (
                          <td
                            key={cell.id}
                            className="p-0"
                            style={{
                              width: COLUMNS[colIndex]?.width,
                              position: "relative",
                              ...lineCellErrorStyle(hasFieldError),
                            }}
                          >
                            {isDropdownColumn(getColumnType(colIndex)) ? (
                              // Dropdown-enabled cell
                              <div className="position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    const newValue = e.target.value;
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      newValue
                                    );
                                    // Show dropdown when typing, but for metalGroup only after 3+ characters
                                    const columnType = getColumnType(colIndex);

                                    // Check if this is a column that requires 3+ characters
                                    const requiresMinLength =
                                      columnType === "metalGroup";
                                    const meetsMinLength =
                                      !requiresMinLength ||
                                      newValue.length >= 3;

                                    // Only show dropdown if minimum length is met (or not required)
                                    if (meetsMinLength) {
                                      setTimeout(() => {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }, 0);
                                    } else {
                                      // Close dropdown if less than 3 characters
                                      setActiveDropdown({
                                        row: null,
                                        col: null,
                                        type: null,
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) =>
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    )
                                  }
                                  onClick={() => {
                                    const columnType = getColumnType(colIndex);
                                    // For metalGroup, only show dropdown if 3+ characters
                                    if (columnType === "metalGroup") {
                                      if (
                                        cell.value &&
                                        cell.value.length >= 3
                                      ) {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }
                                    } else {
                                      // For other columns, show dropdown normally
                                      handleDropdownClick(
                                        rowIndex,
                                        colIndex,
                                        columnType
                                      );
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  autoComplete="off"
                                />
                              </div>
                            ) : isImageColumn(getColumnType(colIndex)) ? (
                              <div
                                className="position-relative d-flex align-items-center justify-content-center"
                                style={{ height: "38px" }}
                              >
                                <input
                                  type="file"
                                  accept="image/*"
                                  ref={(el) =>
                                    (fileInputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  style={{ display: "none" }}
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;

                                    // Check if file is an image
                                    if (!file.type.match("image.*")) {
                                      alert("Please select an image file");
                                      return;
                                    }

                                    // Check file size (limit to 5MB)
                                    if (file.size > 5 * 1024 * 1024) {
                                      alert("File size should not exceed 5MB");
                                      return;
                                    }

                                    handleImageUpload(file, rowIndex);
                                  }}
                                />
                                {cell.imagePreview ? (
                                  <div
                                    className="position-relative"
                                    style={{ height: "38px" }}
                                  >
                                    <div
                                      className="d-flex justify-content-center align-items-center h-100"
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Hide all tooltips first
                                        Object.values(
                                          tooltipRefs.current
                                        ).forEach((tooltip) => {
                                          if (tooltip)
                                            tooltip.style.display = "none";
                                        });

                                        const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Get the position of the image
                                          const rect =
                                            e.currentTarget.getBoundingClientRect();

                                          // Position the tooltip centered below the image
                                          tooltip.style.position = "fixed";
                                          tooltip.style.left =
                                            rect.left +
                                            rect.width / 2 -
                                            100 +
                                            "px"; // Center horizontally (tooltip width is 200px)
                                          tooltip.style.top =
                                            rect.bottom + 10 + "px"; // Position below the image
                                          tooltip.style.display = "block";
                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        // Set a timeout before hiding the tooltip
                                        tooltipTimeoutRef.current = setTimeout(
                                          () => {
                                            // Only hide if this is still the active tooltip
                                            const tooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                            if (activeTooltip === tooltipId) {
                                              const tooltip =
                                                tooltipRefs.current[tooltipId];
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                              }
                                              setActiveTooltip(null);
                                            }
                                          },
                                          300
                                        ); // Delay to allow moving to tooltip
                                      }}
                                    >
                                      <img
                                        src={cell.imagePreview}
                                        alt="Preview"
                                        className="img-thumbnail"
                                        style={{
                                          height: "30px",
                                          width: "30px",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                        onError={(e) => {
                                          const fallback =
                                            getNodeImageCandidates(cell.value)[1] || null;
                                          if (
                                            fallback &&
                                            e.currentTarget.src !== fallback
                                          ) {
                                            e.currentTarget.src = fallback;
                                          }
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openImageModal(
                                            cell.imagePreview,
                                            cell.value,
                                            rowIndex,
                                            colIndex
                                          );
                                        }}
                                      />
                                    </div>

                                    {/* Hover preview tooltip */}
                                    <div
                                      ref={(el) =>
                                        (tooltipRefs.current[
                                          `tooltip-${rowIndex}-${colIndex}`
                                        ] = el)
                                      }
                                      className="shadow rounded p-3 bg-white"
                                      style={{
                                        display: "none",
                                        width: "200px",
                                        zIndex: 9999,
                                        position: "fixed", // Use fixed positioning to escape table constraints
                                      }}
                                      onMouseEnter={(e) => {
                                        // Clear any existing timeout when entering the tooltip
                                        if (tooltipTimeoutRef.current) {
                                          clearTimeout(
                                            tooltipTimeoutRef.current
                                          );
                                          tooltipTimeoutRef.current = null;
                                        }

                                        // Make sure this is the only visible tooltip
                                        const currentTooltipId = `tooltip-${rowIndex}-${colIndex}`;
                                        Object.entries(
                                          tooltipRefs.current
                                        ).forEach(([id, tooltip]) => {
                                          if (
                                            tooltip &&
                                            id !== currentTooltipId
                                          ) {
                                            tooltip.style.display = "none";
                                          }
                                        });

                                        e.currentTarget.style.display = "block";
                                        setActiveTooltip(currentTooltipId);
                                      }}
                                      onMouseLeave={(e) => {
                                        // Hide immediately when leaving the tooltip itself
                                        e.currentTarget.style.display = "none";
                                        setActiveTooltip(null);
                                      }}
                                    >
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: "-8px",
                                          left: "50%",
                                          transform: "translateX(-50%)",
                                          width: "16px",
                                          height: "8px",
                                          overflow: "hidden",
                                        }}
                                      >
                                        <div
                                          className="bg-white border"
                                          style={{
                                            position: "absolute",
                                            top: "0",
                                            left: "0",
                                            width: "16px",
                                            height: "16px",
                                            transform: "rotate(45deg)",
                                            boxShadow:
                                              "-2px -2px 2px rgba(0,0,0,0.1)",
                                            borderRight: "none",
                                            borderBottom: "none",
                                          }}
                                        ></div>
                                      </div>
                                      <div className="text-center mb-3">
                                        <img
                                          src={cell.imagePreview}
                                          alt="Preview"
                                          className="img-fluid rounded"
                                          onError={(e) => {
                                            const fallback =
                                              getNodeImageCandidates(cell.value)[1] || null;
                                            if (
                                              fallback &&
                                              e.currentTarget.src !== fallback
                                            ) {
                                              e.currentTarget.src = fallback;
                                            }
                                          }}
                                          style={{
                                            maxHeight: "100px",
                                            objectFit: "contain",
                                          }}
                                        />
                                      </div>
                                      <div className="d-flex justify-content-center gap-2">
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            triggerFileInput(
                                              rowIndex,
                                              colIndex
                                            );
                                          }}
                                          title="Change Image"
                                        >
                                          <Icon
                                            icon="solar:gallery-edit-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setTableData((prevData) => {
                                              const updatedData = [...prevData];
                                              const imageCell = updatedData[
                                                rowIndex
                                              ].data.find(
                                                (c) => c.id === "image"
                                              );
                                              if (imageCell) {
                                                imageCell.value = "";
                                                imageCell.imageFile = null;
                                                imageCell.imagePreview = null;
                                              }
                                              return updatedData;
                                            });
                                          }}
                                          title="Remove Image"
                                        >
                                          <Icon
                                            icon="solar:trash-bin-trash-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                        <Button
                                          variant="primary"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openImageModal(
                                              cell.imagePreview,
                                              cell.value,
                                              rowIndex,
                                              colIndex
                                            );
                                          }}
                                          title="View Image"
                                        >
                                          <Icon
                                            icon="solar:eye-bold"
                                            width="16"
                                            height="16"
                                          />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() =>
                                      triggerFileInput(rowIndex, colIndex)
                                    }
                                    title="Upload Image"
                                  >
                                    <Icon
                                      icon="solar:gallery-add-bold"
                                      width="16"
                                      height="16"
                                    />
                                  </Button>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : getColumnType(colIndex) === "othAmt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no entries
                                    if (!cell.otherAmountEntries?.length) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    handleKeyDown(
                                      e,
                                      rowIndex,
                                      colIndex,
                                      getColumnType(colIndex)
                                    );
                                    handleOtherAmountInteraction(rowIndex, e);
                                  }}
                                  onDoubleClick={(e) =>
                                    handleOtherAmountInteraction(rowIndex, e)
                                  }
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: "pointer",
                                  }}
                                  onFocus={() =>
                                    handleFocus(rowIndex, colIndex)
                                  }
                                  onBlur={(e) =>
                                    (e.target.style.backgroundColor =
                                      "transparent")
                                  }
                                  title={
                                    cell.otherAmountEntries?.length > 0
                                      ? "Double-click or press Enter twice to edit details"
                                      : "Enter amount directly or double-click/press Enter twice for details"
                                  }
                                />

                                {cell.otherAmountEntries?.length > 0 && (
                                  <div className="position-relative">
                                    <Button
                                      variant="link"
                                      className="p-0 px-1 text-primary"
                                      style={{
                                        fontSize: "1rem",
                                        position: "absolute",
                                        right: "5px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        zIndex: 1,
                                      }}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // If tooltip is already visible, hide it
                                          if (
                                            tooltip.style.display === "block"
                                          ) {
                                            tooltip.style.display = "none";
                                            setActiveTooltip(null);

                                            // Clear any existing timeout
                                            if (tooltipTimeoutRef.current) {
                                              clearTimeout(
                                                tooltipTimeoutRef.current
                                              );
                                              tooltipTimeoutRef.current = null;
                                            }
                                            return;
                                          }

                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);

                                          // Set timeout to hide after 4 seconds
                                          if (tooltipTimeoutRef.current) {
                                            clearTimeout(
                                              tooltipTimeoutRef.current
                                            );
                                          }

                                          tooltipTimeoutRef.current =
                                            setTimeout(() => {
                                              if (tooltip) {
                                                tooltip.style.display = "none";
                                                setActiveTooltip(null);
                                                tooltipTimeoutRef.current =
                                                  null;
                                              }
                                            }, 4000);
                                        }
                                      }}
                                      onMouseEnter={(e) => {
                                        // Don't show on hover if already showing from a click
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (tooltip) {
                                          // Hide any other visible tooltips first
                                          Object.entries(
                                            tooltipRefs.current
                                          ).forEach(([id, t]) => {
                                            if (t && id !== tooltipId) {
                                              t.style.display = "none";
                                            }
                                          });

                                          // Position and show tooltip
                                          positionTooltip(
                                            e.currentTarget,
                                            tooltip
                                          );

                                          // Set this as the active tooltip
                                          setActiveTooltip(tooltipId);
                                        }
                                      }}
                                      onMouseLeave={() => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        const tooltip =
                                          tooltipRefs.current[tooltipId];

                                        if (
                                          tooltip &&
                                          activeTooltip === tooltipId
                                        ) {
                                          // Use setTimeout to allow moving to the tooltip
                                          setTimeout(() => {
                                            // Only hide if we're not hovering over the tooltip itself
                                            if (
                                              activeTooltip === tooltipId &&
                                              !tooltip.matches(":hover") &&
                                              !tooltipTimeoutRef.current
                                            ) {
                                              tooltip.style.display = "none";
                                              setActiveTooltip(null);
                                            }
                                          }, 100);
                                        }
                                      }}
                                    >
                                      <Icon icon="solar:info-circle-bold" />
                                    </Button>

                                    {/* Other Amount Details Tooltip */}
                                    <div
                                      ref={(el) =>
                                        (tooltipRefs.current[
                                          `oth-amt-tooltip-${rowIndex}`
                                        ] = el)
                                      }
                                      className="shadow-lg rounded p-2 bg-white tooltip-container"
                                      style={{
                                        display: "none",
                                        width: "auto",
                                        maxWidth: "250px",
                                        zIndex: 9999,
                                        position: "fixed",
                                      }}
                                      onMouseEnter={() => {
                                        // Do nothing when hovering tooltip
                                        // This prevents the tooltip from disappearing
                                      }}
                                      onMouseLeave={(e) => {
                                        // Only hide if not in clicked mode
                                        if (tooltipTimeoutRef.current) return;

                                        const tooltipId = `oth-amt-tooltip-${rowIndex}`;
                                        if (activeTooltip === tooltipId) {
                                          e.currentTarget.style.display =
                                            "none";
                                          setActiveTooltip(null);
                                        }
                                      }}
                                    >
                                      <div
                                        className="tooltip-arrow"
                                        style={{
                                          position: "absolute",
                                          width: "12px",
                                          height: "12px",
                                          backgroundColor: "white",
                                          border: "1px solid rgba(0,0,0,0.1)",
                                          transform: "rotate(45deg)",
                                          borderRight: "none",
                                          borderBottom: "none",
                                          boxShadow:
                                            "-2px -2px 2px rgba(0,0,0,0.1)",
                                        }}
                                      ></div>
                                      <div
                                        className="other-amount-details"
                                        style={{
                                          maxHeight: "200px",
                                          overflowY: "auto",
                                        }}
                                      >
                                        {cell.otherAmountEntries.map(
                                          (entry, idx) => (
                                            <div
                                              key={idx}
                                              className="d-flex justify-content-between py-1"
                                              style={{
                                                borderBottom:
                                                  idx !==
                                                  cell.otherAmountEntries
                                                    .length -
                                                    1
                                                    ? "1px solid #eee"
                                                    : "none",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  flex: "1",
                                                  marginRight: "20px",
                                                }}
                                              >
                                                {entry.chargeType}
                                              </span>
                                              <span
                                                style={{
                                                  fontSize: "0.85rem",
                                                  minWidth: "80px",
                                                  textAlign: "right",
                                                }}
                                              >
                                                ₹{" "}
                                                {Number(entry.amount).toFixed(
                                                  2
                                                )}
                                              </span>
                                            </div>
                                          )
                                        )}
                                        <div className="mt-2 pt-2 border-top">
                                          <div className="d-flex justify-content-between">
                                            <strong className="text-dark">
                                              Total
                                            </strong>
                                            <strong className="text-dark">
                                              ₹{" "}
                                              {cell.otherAmountEntries
                                                .reduce(
                                                  (sum, entry) =>
                                                    sum + Number(entry.amount),
                                                  0
                                                )
                                                .toFixed(2)}
                                            </strong>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ) : getColumnType(colIndex) === "lsWt" ? (
                              <div className="d-flex align-items-center position-relative">
                                <input
                                  ref={(el) =>
                                    (inputRefs.current[
                                      `${rowIndex}-${colIndex}`
                                    ] = el)
                                  }
                                  type="text"
                                  value={cell.value}
                                  onChange={(e) => {
                                    // Only allow changes if there are no Other Amount entries
                                    if (!hasOtherAmountEntries) {
                                      const value = e.target.value;
                                      if (
                                        value === "" ||
                                        /^\d*\.?\d*$/.test(value)
                                      ) {
                                        handleInputChange(
                                          rowIndex,
                                          colIndex,
                                          value
                                        );
                                      }
                                    }
                                  }}
                                  className="form-control border-0 rounded-0"
                                  style={{
                                    fontSize: "0.85rem",
                                    padding: "0.4rem 0.5rem",
                                    backgroundColor: "transparent",
                                    width: "100%",
                                    transition: "background-color 0.2s",
                                    cursor: hasOtherAmountEntries
                                      ? "not-allowed"
                                      : "text",
                                  }}
                                  onFocus={(e) => {
                                    if (!hasOtherAmountEntries) {
                                      handleFocus(rowIndex, colIndex);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    e.target.style.backgroundColor =
                                      "transparent";
                                  }}
                                  readOnly={hasOtherAmountEntries}
                                  title={
                                    hasOtherAmountEntries
                                      ? "This value is calculated from Other Amount entries"
                                      : ""
                                  }
                                />
                              </div>
                            ) : getColumnType(colIndex) === "totalAmt" ? (
                              <input
                                ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                }
                                type="text"
                                value={cell.value ?? ""}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (
                                    value === "" ||
                                    /^\d*\.?\d*$/.test(value)
                                  ) {
                                    handleInputChange(
                                      rowIndex,
                                      colIndex,
                                      value
                                    );
                                  }
                                }}
                                onKeyDown={(e) => {
                                  handleKeyDown(
                                    e,
                                    rowIndex,
                                    colIndex,
                                    getColumnType(colIndex)
                                  );
                                  handleTotalAmountInteraction(rowIndex, e);
                                }}
                                onDoubleClick={(e) =>
                                  handleTotalAmountInteraction(rowIndex, e)
                                }
                                className="form-control border-0 rounded-0"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                                title={`Double-click or press Enter twice to enter amount details${
                                  cell?.totalAmountDetails?.gst
                                    ? ` (GST ${cell.totalAmountDetails.gst}%)`
                                    : ""
                                }`}
                              />
                            ) : (
                              <input
                                ref={(el) =>
                                  (inputRefs.current[
                                    `${rowIndex}-${colIndex}`
                                  ] = el)
                                }
                                type="text"
                                value={cell.value}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  handleInputChange(
                                    rowIndex,
                                    colIndex,
                                    newValue
                                  );
                                  const columnType = getColumnType(colIndex);
                                  if (isDropdownColumn(columnType)) {
                                    const requiresMinLength =
                                      columnType === "metalGroup";
                                    const meetsMinLength =
                                      !requiresMinLength ||
                                      newValue.length >= 3;
                                    if (meetsMinLength) {
                                      setTimeout(() => {
                                        handleDropdownClick(
                                          rowIndex,
                                          colIndex,
                                          columnType
                                        );
                                      }, 0);
                                    } else {
                                      setActiveDropdown({
                                        row: null,
                                        col: null,
                                        type: null,
                                      });
                                    }
                                  } else {
                                    setActiveDropdown({
                                      row: null,
                                      col: null,
                                      type: null,
                                    });
                                  }
                                }}
                                // disabled={COLUMNS[colIndex].id === "grWt"}
                                className="form-control border-0 rounded-0"
                                style={{
                                  fontSize: "0.85rem",
                                  padding: "0.4rem 0.5rem",
                                  backgroundColor: "transparent",
                                  width: "100%",
                                  transition: "background-color 0.2s",
                                  cursor: "text", // Change cursor to indicate it's editable
                                }}
                                onFocus={() => handleFocus(rowIndex, colIndex)}
                                onBlur={(e) => {
                                  e.target.style.backgroundColor =
                                    "transparent";
                                }}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Add Totals Row */}
                  <tr
                    style={{
                      backgroundColor: "#f8f9fa",
                      position: tableData.length > 6 ? "sticky" : undefined,
                      bottom: tableData.length > 6 ? 0 : undefined,
                      zIndex: tableData.length > 6 ? 5 : undefined,
                    }}
                  >
                    {/* No. */}
                    <td className="p-0" style={{ width: COLUMNS[0]?.width }}>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          Total
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Product Group */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Pcs */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData.reduce((sum, row) => {
                            const pcs = parseFloat(
                              row.data.find((cell) => cell.id === "pcs")
                                ?.value || 0
                            );
                            return sum + (isNaN(pcs) ? 0 : pcs);
                          }, 0)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Gr.Wt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const gwt = parseFloat(
                                row.data.find((cell) => cell.id === "grWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(gwt) ? 0 : gwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Ls.Wt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lwt = parseFloat(
                                row.data.find((cell) => cell.id === "lsWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(lwt) ? 0 : lwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Net.Wt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const nwt = parseFloat(
                                row.data.find((cell) => cell.id === "netWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(nwt) ? 0 : nwt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Purity (average) */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData.length > 0
                            ? (
                                tableData.reduce((sum, row) => {
                                  const purity = parseFloat(
                                    row.data.find(
                                      (cell) => cell.id === "purity"
                                    )?.value || 0
                                  );
                                  return sum + (isNaN(purity) ? 0 : purity);
                                }, 0) / tableData.length
                              ).toFixed(2)
                            : "0.00"}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Wastage (average) */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData.length > 0
                            ? (
                                tableData.reduce((sum, row) => {
                                  const wastage = parseFloat(
                                    row.data.find(
                                      (cell) => cell.id === "wastage"
                                    )?.value || 0
                                  );
                                  return sum + (isNaN(wastage) ? 0 : wastage);
                                }, 0) / tableData.length
                              ).toFixed(2)
                            : "0.00"}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Fine.Wt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const fineWt = parseFloat(
                                row.data.find((cell) => cell.id === "fineWt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(fineWt) ? 0 : fineWt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* RateOn */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* ItRate */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {/* Average item rate - not sum */}
                          {tableData.length > 0
                            ? (
                                tableData.reduce((sum, row) => {
                                  const itRate = parseFloat(
                                    row.data.find(
                                      (cell) => cell.id === "itRate"
                                    )?.value || 0
                                  );
                                  return sum + (isNaN(itRate) ? 0 : itRate);
                                }, 0) / tableData.length
                              ).toFixed(2)
                            : "0.00"}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* ItAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const itAmt = parseFloat(
                                row.data.find((cell) => cell.id === "itAmt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(itAmt) ? 0 : itAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0"></td> {/* Lbr Rate */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* Lbr Amt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const lbrAmt = parseFloat(
                                row.data.find((cell) => cell.id === "lbrAmt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(lbrAmt) ? 0 : lbrAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* OthAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {tableData
                            .reduce((sum, row) => {
                              const othAmt = parseFloat(
                                row.data.find((cell) => cell.id === "othAmt")
                                  ?.value || 0
                              );
                              return sum + (isNaN(othAmt) ? 0 : othAmt);
                            }, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </td>
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* MRP (no total aggregation, left blank) */}
                    </td>
                    <td className="p-0"></td> {/* Image */}
                    <td className="p-0" style={{ backgroundColor: "#fff" }}>
                      {/* TotalAmt */}
                      <div
                        className="d-flex align-items-center justify-content-end px-2"
                        style={{ height: "38px" }}
                      >
                        <span
                          style={{ fontSize: "0.85rem", fontWeight: "600" }}
                        >
                          {calculateTotal().toFixed(2)}
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="text-end mt-3">
              {/* <button className="btn btn-primary" onClick={handleSubmit}>
                Add
              </button> */}
            </div>


            <PaymentSection
              paymentRows={purchasePaymentRows}
              onPaymentRowsChange={setPurchasePaymentRows}
              paymentTypes={paymentTypes}
              bankAccounts={bankAccounts}
              excludedPayModes={["scheme"]}
              payableRows={payableRows}
              onPayableRowsChange={setPayableRows}
              note={note}
              onNoteChange={setNote}
              finalAmount={calculateTotal().toFixed(2)}
              discountType={discountType}
              onDiscountTypeChange={setDiscountType}
              discountValue={discountValue}
              onDiscountValueChange={setDiscountValue}
              discountAmount={calculateDiscountAmount().toFixed(2)}
              gstPercentage={gstPercentage}
              onGstPercentageChange={setGstPercentage}
              gstAmount={calculateGstAmount().total.toFixed(2)}
              roundOffAmount={getDisplayRoundOff()}
              onRoundOffChange={handleRoundOffChange}
              grandTotal={calculateGrandTotal()}
              totalPaid={calculatePurchaseTotalPaid()}
              payableAmount={calculatePurchasePayableAmount()}
              remainingAmount={calculatePurchaseRemaining()}
              followUpDate={followUpDate}
              onFollowUpDateChange={setFollowUpDate}
              buttonClass={buttonClass}
              cardClass={cardClass}
              buttonTableStyle={buttonTableStyle}
            />

            {/* Submit button at the bottom */}
            <div className="row mt-4">
              <div className="col-12">
                <div className="d-flex flex-wrap align-items-center justify-content-between">
                  <div className="d-flex flex-wrap align-items-center">
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="printCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="printCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2m10-8V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4" />
                            <path d="M7 15a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Print</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="pdfCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="pdfCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                            <path d="M5 12V5a2 2 0 0 1 2-2h7l5 5v4M5 18h1.5a1.5 1.5 0 0 0 0-3H5v6m12-3h2m1-3h-3v6m-6-6v6h1a2 2 0 0 0 2-2v-2a2 2 0 0 0-2-2z" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">PDF</span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="whatsappCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="whatsappCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="m3 21l1.65-3.8a9 9 0 1 1 3.4 2.9z" />
                            <path d="M9 10a.5.5 0 0 0 1 0V9a.5.5 0 0 0-1 0za5 5 0 0 0 5 5h1a.5.5 0 0 0 0-1h-1a.5.5 0 0 0 0 1" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">
                          WhatsApp
                        </span>
                      </label>
                    </div>
                    <div className="form-check me-3 mb-2">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="emailCheckbox"
                      />
                      <label
                        className="form-check-label d-flex align-items-center"
                        htmlFor="emailCheckbox"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="currentColor"
                          className="bi"
                          viewBox="0 0 24 24"
                        >
                          <g
                            fill="none"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                          >
                            <path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <path d="m3 7l9 6l9-6" />
                          </g>
                        </svg>
                        <span className="ms-2 d-none d-sm-inline">Email</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <button
                      className={`btn btn-primary ${buttonClass}`}
                      onClick={handleSubmit}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {renderDropdown()}
      <OtherAmountModal
        otherAmountModal={otherAmountModal}
        setOtherAmountModal={setOtherAmountModal}
        cardClass={cardClass}
        otherAmountColumnWidths={otherAmountColumnWidths}
        handleOtherAmountColumnWidthChange={handleOtherAmountColumnWidthChange}
        suggestions={suggestions}
        setSuggestions={setSuggestions}
        getSuggestions={getSuggestions}
        applyChargeType={(entry, chargeName) =>
          applyOtherChargeToEntry(entry, chargeTypes, chargeName)
        }
        chargeOptions={chargeTypes}
        formatAmount={formatAmount}
        calculateAmount={calculateAmount}
        rateOnOptions={rateOnOptions}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        handleRateOnDropdown={handleRateOnDropdown}
        handleRateOnKeyDown={handleRateOnKeyDown}
        calculateLessWeight={calculateLessWeight}
        formatLessWeight={formatLessWeight}
        calculateTotal={sumOtherAmountEntries}
        buttonClass={buttonClass}
        handleSaveOtherAmountEntries={handleSaveOtherAmountEntries}
        setTableData={setTableData}
      />
      {renderTotalAmountModal()}
      {renderPartyDropdown()}
      {renderImageModal()}

      {/* Post-save navigation modal */}
      <Modal
        show={showPostSaveModal}
        onHide={() => handlePostSaveChoice(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Purchase Saved</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Do you want to add this purchase item in the stock?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            className={buttonClass}
            onClick={() => handlePostSaveChoice(false)}
          >
            No
          </Button>
          <Button
            variant="primary"
            className={buttonClass}
            onClick={() => handlePostSaveChoice(true)}
          >
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* <p>Master Gold Rate: {masterGoldRate}</p>
      <p>Master Silver Rate: {masterSilverRate}</p> */}
      <Toaster position="top-center" />
    </div>
  );
};

export default EditPurchase;
